/**
 * The UMED board-parity table (EP-010 §9–§12).
 *
 * == Why this looks like Monday, deliberately =============================
 *
 * Everywhere else in eMerge, reproducing a board's presentation would be a
 * mistake. Here it is the requirement. Josie reconciles high volumes by moving
 * across ten familiar tabs, reading a colour, and scanning a wide row — and
 * that learned fluency is a real operational control, not a habit to be
 * retrained for its own sake. So the tabs are the board's tabs, in the board's
 * order, under the board's names; the colours are the board's colours; and the
 * table scrolls sideways because reconciliation genuinely wants the columns.
 *
 * == What is NOT reproduced ===============================================
 *
 * The architecture. Nothing here moves a row into a group. Every tab is a
 * named predicate over one native case record, decided by the server, and the
 * same predicate serves the tab's badge, its rows and its export — so three
 * people cannot read three numbers and each be right.
 *
 * And one presentation rule the board does not have: the colour never carries
 * a fact by itself. Every chip has its word beside it, and the word eMerge
 * shows is the SOURCE word, never a verdict. The board's APPROVED is coloured
 * and named "Approved in Monday · not approved in eMerge" (P3), because approval is the UMED operator's
 * act of moving the case to Approved, with FLMMIS reflected on record
 * (2026-10-06), and a table cannot be allowed to quietly say otherwise.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  umedApi, personName, shortDate, shortDateTime, boardDate, relativeDay, parseTermDates, STAGE_LABEL, stageLabelOf, NOCA_LABEL, FLMMIS_LABEL, ENROLLMENT_LABEL, PACKET_BLOCKER_OWNER_LABEL,
  canDo, freshKey, parseTermsLine, BoardTabDef, BoardTabKey, BoardPage, BoardRow, BoardSort, LegendEntry, OtherUmed, Scope,
  Noca, Flmmis, NocaResult, FlmmisResult, CorrectionType,
  CaseDisposition, Disposition, DISPOSITION_WORDS, DISPOSITION_LABEL,
  ReversalRequester, REVERSAL_REQUESTER_LABEL, MondayItem, MONDAY_APPROVAL_CLAIM,
} from './umedApi';
import { residentsApi } from '../advocate/residentsApi';
import { MondayDcfEmailMarker } from './MondayDcfEmailMarker';
import { FINANCIAL_RESPONSIBILITY_LABEL, FinancialResponsibilityType } from '../advocate/advocateApi';
// Isaiah Cotto, 2026-10-06: every colour-coded status is FILLED with its Monday colour, from ONE shared map.
import FilledStatusChip from '../../components/status/FilledStatusChip';
import { UMED_STATUS, mondayHex, normalizeMondayWord, filledChipStyle } from '../../theme/mondayPalette';

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/**
 * Copy one value without opening the case.
 *
 * Josie Evans, 2026-09-23: "I also want to be able to right click on
 * everything instead of going into the names, facility name, dcf account &
 * password."
 *
 * Reading a name off a row and retyping it into DCF is the work; opening a
 * case to get it is not. Selecting the text by hand works too -- see the row
 * handler, which stands down when there is a selection -- but a button is one
 * action instead of three and does not depend on a steady hand in a table
 * this dense.
 *
 * The DCF password is deliberately NOT copyable from here. It is not in the
 * list payload at all: it is revealed on one case, for a stated purpose, and
 * audited before anything is decrypted. A copy button on a row would be a way
 * to take a credential without that record existing.
 */
function CopyCell({ value, what }: { value: string; what: string }) {
  const [done, setDone] = useState(false);
  return (
    <button type="button" className="um-copy" aria-label={`Copy ${what}`} title={`Copy ${what}`}
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(value);
          setDone(true);
          window.setTimeout(() => setDone(false), 1200);
        } catch { /* a refused clipboard is not worth an error state: the text is selectable. */ }
      }}>
      {done ? 'Copied' : 'Copy'}
    </button>
  );
}
/**
 * Reveal the DCF password and put it on the clipboard, in one press.
 *
 * Josie Evans, 2026-09-24: "I need to be able to copy and paste both."
 *
 * The password is never rendered and never held in component state longer
 * than the write to the clipboard. Each press is one audited reveal on the
 * server, with the reason recorded -- which is what makes 1,433 stored
 * credentials safe to keep at all.
 */
function CopyPassword({ caseId, disabled }: { caseId: string; disabled?: boolean }) {
  const [state, setState] = useState<'idle' | 'working' | 'done' | 'refused'>('idle');
  if (disabled) return null;
  return (
    <button
      type="button"
      className="um-cred"
      aria-label="Reveal and copy the DCF password"
      title="Reveals the password and copies it. Each reveal is recorded."
      disabled={state === 'working'}
      onClick={async (e) => {
        e.stopPropagation();
        setState('working');
        try {
          const r = await umedApi.revealDcfCredential(caseId, 'Copied from the UMED board to sign in to DCF.');
          if (!r.success || !r.data?.password) { setState('refused'); window.setTimeout(() => setState('idle'), 1800); return; }
          await navigator.clipboard.writeText(r.data.password);
          setState('done');
          window.setTimeout(() => setState('idle'), 1400);
        } catch {
          setState('refused');
          window.setTimeout(() => setState('idle'), 1800);
        }
      }}
    >
      {state === 'working' ? 'Revealing…' : state === 'done' ? 'Copied' : state === 'refused' ? 'Refused' : 'Copy password'}
    </button>
  );
}

/**
 * A generated DCF login, ready for Gwynn to register (UM-R4, 2026-10-02):
 * copy the password, register at DCF, then record it here. Recording keeps the
 * generated password -- nothing is retyped. Two presses, so a stray click on a
 * dense row does not mark an account made.
 */
export function DcfLoginReady({ caseId, name = null }: { caseId: string; name?: string | null }) {
  const acts = React.useContext(BoardActs);
  const [state, setState] = useState<'idle' | 'confirm' | 'working' | 'done' | 'refused'>('idle');
  if (state === 'done') return <>Account made · recorded</>;
  return (
    <>
      Login ready · register at DCF
      <CopyPassword caseId={caseId} />
      <button
        type="button"
        className="um-cred"
        aria-label="Record that this DCF login is registered"
        title="Records the account as made at DCF, keeping the generated password."
        disabled={state === 'working'}
        onClick={async (e) => {
          e.stopPropagation();
          if (state === 'idle' || state === 'refused') { setState('confirm'); return; }
          if (state !== 'confirm') return;
          setState('working');
          try {
            const r = await umedApi.dcfAccountCreated(caseId, {});
            // The button is gone once it is recorded: say so in the board's status line, focus back on the row (P2-4).
            if (r.success) {
              setState('done');
              if (acts) acts.announce(`The DCF login${name ? ` for ${name}` : ''} is recorded as registered.`, caseId);
              return;
            }
          } catch { /* refused below */ }
          setState('refused');
        }}
      >
        {state === 'confirm' ? 'Confirm registered' : state === 'working' ? 'Recording…' : state === 'refused' ? 'Refused' : 'Registered'}
      </button>
    </>
  );
}

const dollars = (v: number | null | undefined) => (v === null || v === undefined ? null : usd.format(v));
const cents = (v: number | null | undefined) => (v === null || v === undefined ? null : usd.format(v / 100));

/* ── The legend ───────────────────────────────────────────────────────── */

/**
 * One status chip, FILLED with the word's Monday colour as Monday shows it
 * (Isaiah Cotto, 2026-10-06), from the one shared map
 * (theme/mondayPalette.ts: Monday's fixed hexes, board 8146038673). The label
 * is white or near-black, whichever reads at 4.5:1 or better on that fill
 * (every UMED word does), so contrast never depends on luck. The word is always
 * rendered: the colour is never the fact by itself.
 */
/*
 * UM-R2 (Josie Evans, 2026-10-02): the label IS the board's word, in her
 * colours, and it moves when she records a native act -- the server writes the
 * word with the act. `source` says who set it: a word set by an act here says
 * so on hover and to a screen reader, because the board and eMerge behave
 * differently on the next sync.
 */
/** What the APPROVED chip says on a case a UMED operator moved to Approved (2026-10-06, P2-6). */
export const APPROVED_IN_EMERGE = 'Approved in eMerge: a UMED operator moved this case to Approved.';
/**
 * What the APPROVED chip says on a case Monday calls approved and eMerge does not (final verification P2-D):
 * the claim's one name (P3) in the tooltip and to a screen reader; the chip itself keeps the short "· Monday's word".
 */
export const APPROVED_MONDAYS_WORD = `${MONDAY_APPROVAL_CLAIM}: nobody has moved this case to Approved here.`;

export function StatusChip({ word, legend, source = null, approvedInEmerge = false }: {
  word: string | null; legend: LegendEntry | null; source?: 'EMERGE' | 'MONDAY' | null;
  /** The case carries the approval act. The board's caveat ("the board's word ...") is then not the truth about it. */
  approvedInEmerge?: boolean;
}) {
  if (!word) return <span className="um-text none">No status</span>;
  const by = source === 'EMERGE' ? 'Recorded in eMerge' : null;
  // Monday's own hex for the word, from the shared map; the server's legend only where the map has none.
  const hex = mondayHex(UMED_STATUS, word) || (legend ? legend.color : null);
  if (!legend || !hex) {
    // A word the legend does not know is shown plainly and never given a
    // guessed colour. It also shows up under the `unmapped` exception, which
    // is where it gets fixed.
    return <span className="um-chip unmapped" title="eMerge's vocabulary does not map this source word.">{word}</span>;
  }
  // APPROVED on a case approved here is eMerge's own word, said so in visible words -- never "the board's word".
  const approvedHere = approvedInEmerge && legend.word === 'APPROVED';
  /*
   * APPROVED on a case nobody approved here is Monday's claim, and says so in
   * visible words and a dashed outline on the fill (final verification
   * 2026-10-06, P2-D; #355's "· Monday's word" stays) -- not only in a tooltip
   * a touch screen never shows.
   */
  const mondaysClaim = !approvedInEmerge && legend.word === 'APPROVED';
  const caveat = approvedHere ? APPROVED_IN_EMERGE : mondaysClaim ? APPROVED_MONDAYS_WORD : legend.caveat;
  const title = [caveat || legend.label, approvedHere ? null : by].filter(Boolean).join(' — ');
  const sr = [caveat ? ` — ${caveat}` : '', by && !approvedHere ? ' (recorded in eMerge)' : ''].join('');
  return (
    <FilledStatusChip word={word} hex={hex} label={legend.label} variant={mondaysClaim ? 'claim' : 'solid'} title={title}
      className={`um-chip${by ? ' native' : ''}${approvedHere ? ' approved' : ''}${mondaysClaim ? ' claim' : ''}`}
      suffix={approvedHere ? <span className="um-chip-by" aria-hidden="true"> · in eMerge</span>
        : mondaysClaim ? <span className="um-chip-by claim" aria-hidden="true"> · Monday&rsquo;s word</span> : null}
      srText={sr || undefined} />
  );
}

/**
 * The disposition a case carries, as a word (2026-10-02).
 *
 * A tag, not a coloured dot: the word is always there, and the tone only
 * echoes it. Who recorded it is said in words too -- "recorded in eMerge" or
 * "the board's word" -- for a screen reader and on hover, because the two
 * behave differently on the next sync and an operator may need to know which
 * one she is looking at.
 */
const DISPOSITION_TONE: Record<CaseDisposition['word'], string> = {
  NO_LONGER_QUALIFIES: 'attn', NEEDS_MEDICAID_RECERT: 'attn', DECEASED: '', PAID_IN_FULL: 'ok', DCF_RESUBMISSION_NEEDED: 'attn', PROBLEM_ACCOUNT: 'attn',
};
/** The disposition chip would only repeat the status word on the row (its board word IS the word shown). */
export const dispositionRepeatsWord = (row: Pick<BoardRow, 'disposition' | 'source_status_raw'>): boolean => {
  const d = row.disposition;
  if (!d) return false;
  const w = DISPOSITION_STATUS_WORD[d.word as Disposition];
  return Boolean(w) && normalizeMondayWord(w) === normalizeMondayWord(row.source_status_raw);
};

export function DispositionChip({ disposition }: { disposition: CaseDisposition | null | undefined }) {
  if (!disposition) return null;
  const by = disposition.source === 'EMERGE' ? 'recorded in eMerge' : 'the board’s word';
  // Filled with the Monday colour of the board word the disposition IS (2026-10-06); the label stays eMerge's words.
  const hex = mondayHex(UMED_STATUS, DISPOSITION_STATUS_WORD[disposition.word as Disposition] || null);
  return (
    <FilledStatusChip word={disposition.word} hex={hex} label={disposition.label} size="sm" title={`${disposition.label} — ${by}`}
      className={`um-tag um-disposition ${DISPOSITION_TONE[disposition.word] || ''}`.trim()} srText={` (${by})`} />
  );
}

export function StatusLegend({ legend }: { legend: LegendEntry[] }) {
  const [open, setOpen] = useState(false);
  if (!legend.length) return null;
  return (
    <div className="um-legend">
      <button type="button" className="um-link" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide' : 'Show'} the status legend
      </button>
      {open && (
        <ul className="um-legend-list" aria-label="Status legend">
          {legend.map((s) => (
            <li key={s.word}>
              {/* The chip exactly as the board shows it: Monday's fill, the word on it (2026-10-06). */}
              <FilledStatusChip word={s.word} hex={mondayHex(UMED_STATUS, s.word) || s.color} label={s.label} size="sm" />
              {/* The label is the board's word now (UM-R2); the word is repeated only where they differ. */}
              {s.label !== s.word && <span className="um-legend-word">{s.word}</span>}
              {s.caveat && <span className="um-legend-caveat">{s.caveat}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ── Sorting ──────────────────────────────────────────────────────────── */

/**
 * A sortable header. Sorting is a view control and changes no business state,
 * which the header says out loud through `aria-sort` rather than only through
 * a caret.
 */
function SortHead({ col, label, page, onSort, className, title }: {
  col: BoardSort | null; label: string; page: BoardPage; onSort: (s: BoardSort, d: 'asc' | 'desc') => void; className?: string; title?: string;
}) {
  if (!col || !page.sorts.includes(col)) return <th scope="col" className={className} title={title}>{label}</th>;
  const on = page.sort === col;
  const next = on && page.dir === 'asc' ? 'desc' : 'asc';
  return (
    <th scope="col" className={className} title={title} aria-sort={on ? (page.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="um-sort" onClick={() => onSort(col, next)}>
        {label}<span className="um-caret" aria-hidden="true">{on ? (page.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>
  );
}

/* ── The SSN, in the table and behind a click ─────────────────────────── */

/**
 * Isaiah, 2026-09-22: "We should make the SSN viewable when clicked on, not
 * necessarily out in the open. It should be in the table and clickable
 * because it is very important for the work they do."
 *
 * So the column is here, and the number is one click away. Four things make
 * that safe rather than merely tidy:
 *
 *   1. **The list payload never carries it.** The board route sends
 *      `ssn_on_file`, a boolean. There is no number in the response that
 *      draws this table, so no amount of scrolling, exporting or
 *      screen-scraping produces one.
 *   2. **Each reveal is its own audited request.** The server writes the
 *      audit row BEFORE it decrypts, naming the operator, the purpose and
 *      this case. Ten revealed rows are ten records of who looked and why.
 *   3. **It is never held.** The value lives in this cell's state and nowhere
 *      else — not in the page's data, not in storage, not in a URL. Moving to
 *      another tab or re-sorting unmounts the cell and the number is gone.
 *   4. **It hides again.** One click shows it, the next puts it away, and a
 *      re-reveal is a second audited request rather than a cached value —
 *      because the audit trail should say how many times it was actually
 *      looked at.
 */
/**
 * What the SERVER said about revealing on this screen: capability AND the
 * `reveal` write group, from GET /residents. `null` means we have not been
 * told yet.
 *
 * It is a context because the column table is a module constant — it cannot
 * close over state — and because a prop threaded through it would still be one
 * answer for the whole table. One request for the board, not one per row.
 */
const CanReveal = React.createContext<boolean | null>(null);

/**
 * `can` is what the SERVER said about revealing here: capability AND the
 * `reveal` write group, from GET /residents. `null` means we have not been
 * told yet.
 *
 * This used to offer the control unconditionally. On 2026-09-22 the `reveal`
 * group was closed in production, so the route did not exist, and every click
 * returned a 404 that this cell rendered as "Could not show it." — which told
 * the operator nothing and told us nothing either. A screen offers nothing it
 * has not been told it may do, and when a refusal does arrive it says which
 * refusal it was.
 */
function SsnCell({ row }: { row: BoardRow }) {
  const can = React.useContext(CanReveal);
  const [value, setValue] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // A different resident in this cell's position is a different secret.
  const [copied, setCopied] = useState(false);
  useEffect(() => { setValue(null); setErr(null); setCopied(false); }, [row.id]);

  if (!row.ssn_on_file) {
    return row.readiness_blockers.includes('SSN_REQUIREMENT')
      ? <span className="um-tag attn">Needed</span>
      : <span className="um-text none">Not on file</span>;
  }
  // On file, but not for this caller on this screen. Say that it exists — that
  // is a fact the operator needs — without dangling a control that cannot work.
  if (can === false) return <span className="um-text none" title="On file. Revealing it is not switched on for you here.">On file</span>;
  if (value) {
    return (
      <span className="um-ssn">
        <span className="um-mono">{value}</span>
        <CopyCell value={value} what={`the SSN for ${personName(row)}`} />
        <button type="button" className="um-link" onClick={() => setValue(null)} aria-label={`Hide the SSN for ${personName(row)}`}>Hide</button>
      </span>
    );
  }
  return (
    <span className="um-ssn">
      <button type="button" className="um-reveal" disabled={busy || !row.patient_id} aria-label={`Show the SSN for ${personName(row)}`} onClick={async () => {
        if (!row.patient_id) return;
        setBusy(true); setErr(null);
        try {
          const r = await residentsApi.revealSsn(row.patient_id, { purpose: 'UMED_PROCESSING', context_type: 'UMED_CASE', context_id: row.id });
          if (r.success && r.data) setValue(r.data.ssn);
          // Each refusal is a different thing to do about it: ask for the act
          // to be switched on, ask for access, or try again.
          else if (r.status === 404) setErr('Not switched on here.');
          else if (r.code === 'SSN_NOT_ON_FILE') setErr('Not on file.');
          else if (r.code === 'DENY_NO_CAPABILITY' || r.status === 403) setErr('Not permitted.');
          else setErr(r.message || 'Could not show it.');
        } catch { setErr('Could not reach the server.'); } finally { setBusy(false); }
      }}>{busy ? '…' : '•••-••-••••'}</button>
      {/*
        * Copy it without showing it.
        *
        * Josie Evans, 2026-09-29: "The S.S# is fine if it's hidden but I should
        * be able to still copy & paste it if I need to run it in FLMMIS. Right
        * now it's not letting me copy & paste."
        *
        * A clipboard copy IS a disclosure, so this goes through the SAME
        * audited reveal as the eye — same purpose, same case context, same
        * audit row. The only difference is that the digits reach her clipboard
        * instead of her screen, which is what she actually needs for FLMMIS and
        * is strictly less exposure than revealing it on a shared monitor.
        */}
      <button type="button" className="um-link" disabled={busy || !row.patient_id}
        aria-label={`Copy the SSN for ${personName(row)} without showing it`}
        title="Copies to the clipboard without showing it. Recorded in the access log, the same as showing it."
        onClick={async (ev) => {
          ev.stopPropagation();
          if (!row.patient_id) return;
          setBusy(true); setErr(null);
          try {
            const r = await residentsApi.revealSsn(row.patient_id, { purpose: 'UMED_PROCESSING', context_type: 'UMED_CASE', context_id: row.id });
            if (r.success && r.data) {
              await navigator.clipboard.writeText(r.data.ssn);
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1400);
            } else if (r.status === 404) setErr('Not switched on here.');
            else if (r.code === 'SSN_NOT_ON_FILE') setErr('Not on file.');
            else if (r.code === 'DENY_NO_CAPABILITY' || r.status === 403) setErr('Not permitted.');
            else setErr(r.message || 'Could not copy it.');
          } catch { setErr('Could not copy it.'); } finally { setBusy(false); }
        }}>{copied ? 'Copied' : 'Copy'}</button>
      {err && <span className="um-text none" role="alert">{err}</span>}
    </span>
  );
}

/* ── Recording an outcome from the row ────────────────────────────────── */

/**
 * What this caller may record from a row, and how the board hears that it
 * changed. One answer for the table, like CanReveal, because the column set
 * is a module constant and cannot close over state.
 */
interface BoardActsValue {
  canReview: boolean;
  canCorrect: boolean;
  canMedicaid: boolean;
  /** Isaiah Cotto, 2026-09-28: anyone with UMED access may settle terms. */
  canTerms: boolean;
  /** 2026-10-06: record a sending Monday knows about and eMerge does not (umed.submission.prepare). */
  canMarkSubmitted: boolean;
  openCase: (id: string) => void;
  /** Attach a NOCA from the row (umed.document.write), 2026-09-28. */
  canDocument: boolean;
  /** The correction types the SERVER offers. SFS is the one Josie named. */
  sfs: boolean;
  /** Record a disposition (UMED_APPROVAL_REVIEW, write group `disposition`), 2026-10-02. */
  canDispose: boolean;
  /** UM-R2: request, send and answer a reversal from the row (umed.reversal.manage). */
  canReverse: boolean;
  /** Who may ask for a reversal, as the SERVER offers them. */
  requesters: ReversalRequester[];
  /** The FLMMIS-issue correction type, when the server offers it. */
  flmmisIssue: boolean;
  recheckDays: number;
  reload: () => void;
  /**
   * Say what a row act did, in the board's one status line, re-read the board,
   * and put focus back on that row's name -- or on the line itself when the row
   * has left the tab (design verification 2026-10-06, P1-b). An act whose
   * control disappears on success must never leave focus on the page body.
   */
  announce: (msg: string, rowId: string, opts?: AnnounceOpts) => void;
}
/**
 * `keepFocus`: leave focus where it is (the control is still there and still has it).
 * `focusTo`: after the re-read, put focus HERE -- the control that opened the act (verification 2026-10-06,
 * P2-4) -- falling back to the row's name, then the status line, when that control is gone.
 */
interface AnnounceOpts { keepFocus?: boolean; focusTo?: () => HTMLElement | null }

/**
 * Focus back on the control that opened an in-row editor once it closes -- on Cancel, Escape, or after the act
 * (P2-4). The ref goes on the opener; `restore()` is called as the editor closes.
 */
function useReturnFocus<T extends HTMLElement>(open: boolean) {
  const opener = React.useRef<T | null>(null);
  const pending = React.useRef(false);
  useEffect(() => {
    if (!open && pending.current) { pending.current = false; opener.current?.focus(); }
  }, [open]);
  return { opener, restore: () => { pending.current = true; } };
}
const BoardActs = React.createContext<BoardActsValue | null>(null);

/**
 * Run `reset` when the row in this cell's position becomes ANOTHER case -- never on the first run (round 4): a
 * mount-time reset that is flushed late would close a grid or a drop highlight the operator had just opened.
 */
function useOnRowChange(id: string, reset: () => void) {
  const seen = React.useRef(id);
  useEffect(() => {
    if (seen.current === id) return;
    seen.current = id;
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
}

/** The approval act's one result sentence, whichever control made it (P2-6). */
const approvedSentence = (name: string, replayed: boolean) =>
  (replayed ? `${name} was already approved.` : `${name} is approved. The Finance handoff is open.`);

/** A row's evidence is kept as it was once the case is approved (verification 2026-10-06, P1-A). */
const isApproved = (row: Pick<BoardRow, 'approval'>) => row.approval.canonical === 'PROVEN';
export const APPROVED_LOCK = 'Approved: kept as recorded. To undo an approval, record a reversal or correction.';

/** Escape on a confirmation cancels it, like its Cancel button (P2-2). */
const escapeCancels = (cancel: () => void) => (e: React.KeyboardEvent) => {
  if (e.key === 'Escape') { e.stopPropagation(); cancel(); }
};

/**
 * Today where the OPERATOR is, never `toISOString().slice(0, 10)`.
 *
 * Eastern time is behind UTC, so after 20:00 ET that call returns tomorrow —
 * and a date field whose maximum is tomorrow is a date field that accepts a
 * follow-up nobody has made yet.
 */
function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * The two approval facts as a REVIEW would state them.
 *
 * The case's vocabulary is wider than a review's: a case can be REJECTED,
 * EXCEPTION, or WITHHELD (withheld meaning this caller may not read it).
 * A review has no word for any of those, so those cases return null and the
 * quick controls stand down rather than carry a fact forward as something it
 * is not. Recording "not recorded" over a rejected NOCA would be eMerge
 * erasing a fact to keep a button working.
 */
/** The three answers a review can give, in the words the case detail uses. */
const NOCA_RESULT_WORDS: Record<NocaResult, string> = {
  NOT_RECORDED: 'Not recorded', PENDING: 'Pending', RECEIVED: 'Received',
};
const FLMMIS_RESULT_WORDS: Record<FlmmisResult, string> = {
  NOT_RECORDED: 'Not recorded', NOT_REFLECTED: 'Not reflected', REFLECTED: 'Reflected',
};

const nocaForward = (n: Noca): NocaResult | null =>
  (n === 'RECEIVED' ? 'RECEIVED' : n === 'PENDING' ? 'PENDING' : n === 'UNKNOWN' ? 'NOT_RECORDED' : null);
const flmmisForward = (f: Flmmis): FlmmisResult | null =>
  (f === 'REFLECTED' ? 'REFLECTED' : f === 'NOT_REFLECTED' ? 'NOT_REFLECTED' : f === 'UNKNOWN' ? 'NOT_RECORDED' : null);

/**
 * Josie Evans, 2026-09-24: "Where the UMED status is I want to be able to
 * drop a box down that says approval, SFS correction, or noca-no flmmis."
 *
 * On the board that dropdown MOVED the row: you picked a word and the word
 * became true. Here it cannot, and should not. Every tab on this screen is a
 * predicate over the case record, so a status is something the case IS, never
 * something it was told it is.
 *
 * So the three words she named are offered as the three ACTS that produce
 * them — a review that finds FLMMIS reflected, a review that finds the NOCA
 * alone, a correction raised with its reason — and the chip beside them keeps
 * showing the board's own word until the facts move it. She gets her dropdown;
 * nothing gets declared true by being picked from a list.
 *
 * Each choice opens a panel that says what will happen BEFORE it happens.
 * "Approval" opens the Finance handoff, and a select that fires on change is
 * no place to learn that afterwards.
 */
/*
 * UM-R2 (Josie Evans, 2026-10-02): "Please provide me these status updates
 * with this color coding." The menu now offers every board word she listed
 * that a native act can produce, each NAMED by the word it produces -- and,
 * as before, each is the ACT, never the word declared: REVERSAL raises a
 * reversal with its requester and reason, REVERSAL RESUB records the sending,
 * SFS CORRECTION COMPLETED completes the open correction. The server writes the
 * word in the same transaction (statusWords.js), so the chip moves when the
 * board reloads. Offered only where the act can work: a correction is
 * requested when none is open and completed when one is; a reversal is
 * requested, sent, or answered according to where it stands.
 */
type Outcome =
  | 'APPROVAL' | 'NOCA_NO_FLMMIS'
  | 'SFS_CORRECTION' | 'SFS_CORRECTION_FLMMIS_ISSUE' | 'CORRECTION_COMPLETE'
  | 'REVERSAL_REQUEST' | 'REVERSAL_SUBMITTED' | 'REVERSAL_APPROVED' | 'REVERSAL_NOT_APPROVED';
const OUTCOME_LABEL: Record<Outcome, string> = {
  APPROVAL: 'APPROVED',
  NOCA_NO_FLMMIS: 'NOCA-NO FLMMIS',
  SFS_CORRECTION: 'SFS CORRECTION',
  SFS_CORRECTION_FLMMIS_ISSUE: 'SFS CORRECTION-FLMMIS ISSUE',
  CORRECTION_COMPLETE: 'SFS CORRECTION COMPLETED',
  REVERSAL_REQUEST: 'REVERSAL',
  REVERSAL_SUBMITTED: 'REVERSAL RESUB',
  REVERSAL_APPROVED: 'REVERSAL COMPLETE',
  REVERSAL_NOT_APPROVED: 'REVERSAL NOT APPROVED',
};
/** A paid-in-full correction completes as Finance's word for it. */
const outcomeLabel = (o: Outcome, row: BoardRow): string =>
  (o === 'CORRECTION_COMPLETE' && row.correction?.correction_type === 'PAID_IN_FULL_CORRECTION' ? 'PAID IN FULL CORRECTION' : OUTCOME_LABEL[o]);

/** The acts this caller may record on this row, in the order the menu offers them. */
function outcomesFor(row: BoardRow, acts: BoardActsValue | null): Outcome[] {
  if (!acts) return [];
  const out: Outcome[] = [];
  /*
   * APPROVED is the approval act itself (2026-10-06), offered only where it can
   * work: FLMMIS reflected on record and not yet approved (P2-6) -- never a menu
   * entry that opens only to say no. NOCA-NO FLMMIS rewrites the evidence, so an
   * approved case is not offered it (P1-A; the server refuses it too).
   */
  if (acts.canReview) {
    if (row.approval.ready_to_approve && !isApproved(row)) out.push('APPROVAL');
    if (!isApproved(row)) out.push('NOCA_NO_FLMMIS');
  }
  if (acts.canCorrect) {
    if (row.correction) out.push('CORRECTION_COMPLETE');
    else {
      if (acts.sfs) out.push('SFS_CORRECTION');
      if (acts.flmmisIssue) out.push('SFS_CORRECTION_FLMMIS_ISSUE');
    }
  }
  if (acts.canReverse && row.stage !== 'CLOSED') {
    const v = row.reversal;
    if (!v) out.push('REVERSAL_REQUEST');
    else if (v.state === 'REQUESTED' || v.state === 'NOT_APPROVED') out.push('REVERSAL_SUBMITTED');
    else if (v.state === 'SUBMITTED') out.push('REVERSAL_APPROVED', 'REVERSAL_NOT_APPROVED');
  }
  return out;
}

const OUTCOME_EFFECT: Partial<Record<Outcome, string>> = {
  SFS_CORRECTION: 'Raises an SFS correction: a new UMED must be produced, and whoever makes packets is told. The reason is kept with the correction for good. The case moves to Corrections and out of Review for approvals until the correction is complete.',
  SFS_CORRECTION_FLMMIS_ISSUE: 'Raises a correction because FLMMIS rejected it: a new UMED must be produced, and whoever makes packets is told. The reason is kept for good. The case moves to Corrections and New approvals — NOCA, and out of Review for approvals.',
  // Said per case: see correctionCompleteEffect (an approved case stays approved).
  REVERSAL_REQUEST: 'Raises a reversal: who asked and why are kept with it, the approval and Finance facts as they stand are frozen on it, and producing the reversal packet opens. The case moves to Reversals and out of Review for approvals.',
  REVERSAL_SUBMITTED: 'Records that the reversal packet went to DCF. It stays under Reversals until DCF answers.',
  REVERSAL_APPROVED: 'Records that DCF approved the reversal: it is complete. If Finance had anything to unwind, the Finance handoff opens.',
  REVERSAL_NOT_APPROVED: 'Records that DCF did not approve the reversal. It stays under Reversals until it is sent again or left.',
};

/**
 * SFS CORRECTION COMPLETED, in the words that are true for THIS case
 * (verification of 2026-10-06, P2-5). Completing a correction opens its own
 * Finance handoff either way; what happens to the case depends on whether it
 * is approved in eMerge -- an approved case stays approved and never goes
 * back to Review for approvals, which holds only cases not yet approved.
 */
export const correctionCompleteEffect = (approved: boolean) => (approved
  ? 'Records that the corrected UMED went in and the correction is complete, and opens the Finance handoff for the correction. The case is approved in eMerge and stays approved: it does not go back to Review for approvals.'
  : 'Records that the corrected UMED went in and the correction is complete, and opens the Finance handoff for the correction. The case is not approved yet, so it goes back to Review for approvals while DCF answers.');

function OutcomePanel({ row, outcome, onClose }: { row: BoardRow; outcome: Outcome; onClose: () => void }) {
  const acts = React.useContext(BoardActs);
  const [on, setOn] = useState(todayLocal());
  const [reason, setReason] = useState('');
  const [requester, setRequester] = useState<ReversalRequester | ''>('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const label = outcomeLabel(outcome, row);
  /*
   * The server does not write NOCA-NO FLMMIS over an open reversal, correction
   * or problem account (statusWords.reviewWord), so the panel does not promise
   * it. Every other act writes its own word. APPROVED is not recorded here: it
   * is the Approve act's own confirmation (ApproveAct), one sentence for both
   * ways in (P2-6).
   */
  const exceptionWork = Boolean(row.reversal) || Boolean(row.correction)
    || (['REVERSAL', 'CORRECTION', 'PROBLEM'].includes(row.exception) && row.exception_state !== 'COMPLETE');
  const wordStays = outcome === 'NOCA_NO_FLMMIS' && exceptionWork;
  const isReview = outcome === 'NOCA_NO_FLMMIS';
  const needsReason = outcome === 'SFS_CORRECTION' || outcome === 'SFS_CORRECTION_FLMMIS_ISSUE' || outcome === 'REVERSAL_REQUEST';
  const ready = (!needsReason || reason.trim().length >= 3) && (outcome !== 'REVERSAL_REQUEST' || Boolean(requester));

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      let r: { success: boolean; status?: number; code?: string; message?: string; error?: string };
      if (outcome === 'SFS_CORRECTION' || outcome === 'SFS_CORRECTION_FLMMIS_ISSUE') {
        r = await umedApi.requestCorrection(row.id, { correction_type: outcome as CorrectionType, reason: reason.trim() });
      } else if (outcome === 'CORRECTION_COMPLETE') {
        if (!row.correction) return;
        r = await umedApi.completeCorrection(row.correction.id);
      } else if (outcome === 'REVERSAL_REQUEST') {
        r = await umedApi.requestReversal(row.id, { requester: requester as ReversalRequester, reason: reason.trim() });
      } else if (outcome === 'REVERSAL_SUBMITTED') {
        if (!row.reversal) return;
        r = await umedApi.reversalSubmitted(row.reversal.id);
      } else if (outcome === 'REVERSAL_APPROVED' || outcome === 'REVERSAL_NOT_APPROVED') {
        if (!row.reversal) return;
        r = await umedApi.reversalOutcome(row.reversal.id, outcome === 'REVERSAL_APPROVED' ? 'APPROVED' : 'NOT_APPROVED');
      } else {
        r = await umedApi.review(row.id, { noca: 'RECEIVED' as NocaResult, flmmis: 'NOT_REFLECTED' as FlmmisResult, decision: 'CONTINUE', follow_up_on: on });
      }
      if (!r.success) { setErr(refusal(r)); return; }
      onClose();
      acts?.announce(`${label} recorded for ${personName(row)}.`, row.id);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };

  return (
    <div className="um-rowpanel" role="group" aria-label={`Record ${label} for ${personName(row)}`}
      onClick={(e) => e.stopPropagation()} onKeyDown={busy ? undefined : escapeCancels(onClose)}>
      <p className="um-rowpanel-what">
        {outcome === 'NOCA_NO_FLMMIS' && `Records the NOCA as received and FLMMIS as not reflected. The case is not approved, and the next check falls due ${acts?.recheckDays ?? 10} days after the date below.`}
        {outcome === 'CORRECTION_COMPLETE' ? correctionCompleteEffect(isApproved(row)) : OUTCOME_EFFECT[outcome]}
        {' '}{wordStays
          ? <>The status stays {row.source_status_raw || 'as it is'}: an open reversal, correction or problem account keeps its own word.</>
          : <>The status becomes {label}.</>}
      </p>
      {outcome === 'REVERSAL_REQUEST' && (
        <label className="um-rowfield"><span>Who asked</span>
          <select value={requester} disabled={busy} onChange={(e) => setRequester(e.target.value as ReversalRequester)} aria-label="Who asked for the reversal">
            <option value="">Choose…</option>
            {(acts?.requesters || []).map((k) => <option key={k} value={k}>{REVERSAL_REQUESTER_LABEL[k]}</option>)}
          </select>
        </label>
      )}
      {needsReason && (
        <label className="um-rowfield"><span>Reason</span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} disabled={busy}
            placeholder={outcome === 'REVERSAL_REQUEST' ? 'Why it must be reversed' : 'Why it must be corrected'}
            aria-label={outcome === 'REVERSAL_REQUEST' ? 'Why the reversal is needed' : 'Why the correction is needed'} />
        </label>
      )}
      {isReview && (
        <label className="um-rowfield"><span>The day you looked</span>
          <input type="date" value={on} max={todayLocal()} disabled={busy}
            onChange={(e) => setOn(e.target.value)} aria-label="The day you checked for an approval" />
        </label>
      )}
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        <button type="button" className="um-open um-primary" disabled={busy || !ready}
          onClick={save}>{busy ? 'Recording…' : 'Record'}</button>
        <button type="button" className="um-link" disabled={busy} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

/**
 * A disposition, from the row (2026-10-02).
 *
 * The same discipline as OutcomePanel: the panel says what will happen before
 * it happens, a reason is required for every one (the server refuses without
 * it, so the button does too), and the effective date defaults to today.
 * After saving the board reloads, and the row leaves this tab when the tab's
 * predicate no longer holds -- which is the point: a resident who no longer
 * qualifies is not in Review for approval any more.
 *
 * REACTIVATE is offered only where the server will accept it (the row's
 * `disposition.reactivatable`), and a closed case is offered nothing else.
 */
const DISPOSITION_EFFECT: Record<Disposition, string> = {
  NO_LONGER_QUALIFIES: 'Suspends the approval chase. The case keeps its stage, leaves every approval, creation and submit queue, and is found under Not qualifying / recert and in All cases.',
  NEEDS_MEDICAID_RECERT: 'Suspends the case until Medicaid is recertified. It leaves every approval, creation and submit queue and is found under Not qualifying / recert and in All cases.',
  DECEASED: 'Closes the case on the date below. It leaves every queue and stays in All cases. The Patient Advocate’s case is not changed and nothing is sent.',
  PAID_IN_FULL: 'Records the account as paid in full; the case leaves the approval chase. Finance’s own payment records are not touched and no Finance handoff is opened.',
  DCF_RESUBMISSION_NEEDED: 'Opens a DCF resubmission and tells Gwynn to send it again. The case moves to the DCF RESUB NEEDED section and stays in Review for Approvals (Josie Evans, 2026-10-06).',
  // UM-R1/UM-R2 (Josie Evans, 2026-09-28 and 2026-10-02).
  PROBLEM_ACCOUNT: 'Marks a problem account: it goes back to the Patient Advocate to contact the facility and the POA, with your reason as the note. The case leaves Review for approvals and is found under Problem Accounts and in All cases. Reactivate puts it back.',
  REACTIVATE: 'Undoes the disposition: what it changed goes back to what it replaced, so the case returns to the queues it was in. Anything that has moved since stays as it is now, and an approval recorded since keeps the case approved.',
};
/** The board word each disposition writes (UM-R2), said in the panel so the chip's change is not a surprise. */
const DISPOSITION_STATUS_WORD: Partial<Record<Disposition, string>> = {
  NO_LONGER_QUALIFIES: 'NO LONGER QUALIFIES', NEEDS_MEDICAID_RECERT: 'NEEDS MCD RECERT', DECEASED: 'DECEASED',
  PAID_IN_FULL: 'PAID IN FULL', DCF_RESUBMISSION_NEEDED: 'DCF RESUB NEEDED', PROBLEM_ACCOUNT: 'PROBLEM ACCOUNT',
};

/**
 * The dispositions an approved case refuses (409 ALREADY_APPROVED,
 * nativeWorkflowService CONTRADICTS_APPROVAL, verification of 2026-10-06
 * P2-2): each says the case is not, or not yet, approvable. Not offered on an
 * approved row -- undoing an approval is a reversal or a correction.
 */
export const CONTRADICTS_APPROVAL: readonly Disposition[] = ['DCF_RESUBMISSION_NEEDED', 'NO_LONGER_QUALIFIES', 'NEEDS_MEDICAID_RECERT', 'PROBLEM_ACCOUNT'];

function DispositionPanel({ row, onClose, initial = null }: { row: BoardRow; onClose: () => void; initial?: Disposition | null }) {
  const acts = React.useContext(BoardActs);
  const closed = row.stage === 'CLOSED';
  const approved = isApproved(row);
  const choices: Disposition[] = [
    ...(closed ? [] : DISPOSITION_WORDS.filter((w) => w !== row.disposition?.word && !(approved && CONTRADICTS_APPROVAL.includes(w)))),
    ...(row.disposition?.reactivatable ? ['REACTIVATE' as Disposition] : []),
  ];
  const [choice, setChoice] = useState<Disposition | ''>(
    initial && choices.includes(initial) ? initial : (choices.length === 1 ? choices[0] : ''),
  );
  const [reason, setReason] = useState('');
  const [on, setOn] = useState(todayLocal());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // One key per opening of the panel: a retry of this click is a replay, not a second act.
  const [key] = useState(() => freshKey());

  const save = async () => {
    if (!choice) return;
    setBusy(true); setErr(null);
    try {
      const r = await umedApi.recordDisposition(row.id, { disposition: choice, reason: reason.trim(), effective_on: on, idempotency_key: key });
      if (!r.success) { setErr(refusal(r)); return; }
      onClose();
      acts?.announce(`${DISPOSITION_LABEL[choice]} recorded for ${personName(row)}.`, row.id);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };

  const boardMoves = (row.forward_authority || 'MONDAY') === 'MONDAY';
  return (
    <div className="um-rowpanel" role="group" aria-label={`Record a disposition for ${personName(row)}`}
      onClick={(e) => e.stopPropagation()} onKeyDown={busy ? undefined : escapeCancels(onClose)}>
      {choices.length === 0 ? (
        <p className="um-rowpanel-what">This case is closed, and its closing cannot be undone here.</p>
      ) : (
        <>
          <label className="um-rowfield"><span>Disposition</span>
            <select value={choice} disabled={busy} onChange={(e) => setChoice(e.target.value as Disposition)} aria-label="Disposition">
              <option value="">Choose…</option>
              {choices.map((d) => <option key={d} value={d}>{DISPOSITION_LABEL[d]}</option>)}
            </select>
          </label>
          {choice && (
            <p className="um-rowpanel-what">
              {DISPOSITION_EFFECT[choice]}
              {DISPOSITION_STATUS_WORD[choice] && ` The status becomes ${DISPOSITION_STATUS_WORD[choice]}.`}
              {choice === 'REACTIVATE' && boardMoves && ' This case still follows the Monday board: if the board still carries the word, the next sync applies it again.'}
            </p>
          )}
          <label className="um-rowfield"><span>Reason</span>
            <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} disabled={busy}
              placeholder={choice === 'PROBLEM_ACCOUNT' ? 'The note for the Patient Advocate — kept with the case' : 'Why — kept with the case'} aria-label="Why this disposition" />
          </label>
          <label className="um-rowfield"><span>Effective</span>
            <input type="date" value={on} max={todayLocal()} disabled={busy}
              onChange={(e) => setOn(e.target.value)} aria-label="The day it took effect" />
          </label>
        </>
      )}
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        {choices.length > 0 && (
          <button type="button" className="um-open um-primary" disabled={busy || !choice || reason.trim().length < 3}
            onClick={save}>{busy ? 'Recording…' : 'Record'}</button>
        )}
        <button type="button" className="um-link" disabled={busy} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

/** Each refusal is a different thing to do about it. */
const refusal = (r: { status?: number; code?: string; message?: string; error?: string }): string => {
  if (r.status === 404) return 'Not switched on here.';
  if (r.code === 'DENY_NO_CAPABILITY' || r.status === 403) return 'Not permitted.';
  if (r.code === 'CORRECTION_OPEN') return 'A correction is already open on this case.';
  if (r.code === 'REVERSAL_OPEN') return 'A reversal is already open on this case.';
  if (r.code === 'EXCEPTION_ALREADY_OPEN') return r.message || r.error || 'Another exception is open on this case.';
  return r.message || r.error || 'That was not recorded.';
};

type RowAct = Outcome | 'DISPOSITION' | 'PROBLEM_ACCOUNT' | 'DCF_RESUB' | 'SUBMISSION';

/* ── Approval is an explicit act (Josie Evans, 2026-10-06) ───────────── */

/**
 * "I want to be able to click down on FLMMIS reflecting, but I do not want it
 * to move to approval unless I click UMED status and then move it to
 * approval."
 *
 * So FLMMIS reflected is recorded as evidence (FlmmisCell, with its own
 * in-row confirmation), and the approval is THIS: one clear button, offered
 * only when FLMMIS is on record and nobody has approved the case yet, that
 * says what it will do before it does it. No browser dialog -- a confirmation
 * in the row, where her eyes already are, with the consequence written out.
 *
 * APPROVED chosen from the status menu opens this same confirmation
 * (`startConfirming`), so both ways in say one sentence and do one thing
 * (design verification 2026-10-06, P2-6).
 *
 * Exported so the case panel offers the same act with the same words.
 */
export const APPROVE_EFFECT = (name: string) => `Approves ${name}: the status becomes APPROVED and the Finance handoff opens.`;
export const FLMMIS_EFFECT = 'Records FLMMIS as reflected. The case is not approved until you move it to Approved.';
/** Where the next act is, said in the FLMMIS confirmation on the row (P1-a). */
export const FLMMIS_NEXT = 'Approve it in UMED status when ready.';

export function ApproveAct({ row, onDone, compact = false, startConfirming = false, onCancel }: {
  row: Pick<BoardRow, 'id' | 'first_name' | 'last_name' | 'approval' | 'stage'>;
  onDone: (msg: string) => void;
  compact?: boolean;
  /** Open straight on the confirmation (the status menu's APPROVED). */
  startConfirming?: boolean;
  /** Who takes focus back on Cancel or Escape, when the opener is not this button. */
  onCancel?: () => void;
}) {
  const [confirming, setConfirming] = useState(startConfirming);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const opener = React.useRef<HTMLButtonElement | null>(null);
  const refocus = React.useRef(false);
  useEffect(() => { setConfirming(startConfirming); setErr(null); }, [row.id, startConfirming]);
  // Cancel puts focus back on the button that opened the confirmation, never on the page body (P2-1).
  useEffect(() => {
    if (!confirming && refocus.current) { refocus.current = false; opener.current?.focus(); }
  }, [confirming]);
  const name = personName(row);
  if (row.approval.canonical === 'PROVEN' || row.stage === 'CLOSED') return null;
  const ready = Boolean(row.approval.ready_to_approve);
  const cancel = () => {
    setErr(null);
    if (onCancel) { onCancel(); return; }
    refocus.current = true;
    setConfirming(false);
  };

  if (!confirming) {
    return (
      <span className={`um-approve${compact ? ' compact' : ''}`} onClick={(e) => e.stopPropagation()}>
        <button type="button" className="um-approve-btn" disabled={!ready} ref={opener}
          aria-label={`Approve ${name}`}
          title={ready ? 'Move this case to Approved.' : 'Record FLMMIS reflected first.'}
          onClick={() => { setErr(null); setConfirming(true); }}>
          Approve
        </button>
        {!ready && !compact && <span className="um-sub">Record FLMMIS reflected first.</span>}
      </span>
    );
  }
  return (
    <div className="um-rowpanel um-confirm" role="group" aria-label={`Approve ${name}`}
      onClick={(e) => e.stopPropagation()} onKeyDown={busy ? undefined : escapeCancels(cancel)}>
      <p className="um-rowpanel-what">{APPROVE_EFFECT(name)}</p>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        <button type="button" className="um-open um-primary" disabled={!ready} aria-disabled={busy} autoFocus
          onClick={async () => {
            // aria-disabled, not disabled, while busy: a disabled button drops focus to the page body, and a refusal then leaves her nowhere.
            if (busy) return;
            setBusy(true); setErr(null);
            try {
              const r = await umedApi.approve(row.id);
              if (!r.success) { setErr(r.code === 'FLMMIS_REQUIRED' ? 'Record FLMMIS reflected first.' : refusal(r)); return; }
              setConfirming(false);
              onDone(approvedSentence(name, Boolean(r.data?.replayed)));
            } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
          }}>{busy ? 'Approving…' : 'Approve'}</button>
        <button type="button" className="um-link" disabled={busy} onClick={cancel}>Cancel</button>
      </div>
    </div>
  );
}

/**
 * Monday says a later stage than eMerge (Josie Evans, 2026-10-06: "He was under
 * Ready to submit in eMerge but under Review for Approvals in Monday").
 *
 * Monday no longer moves a stage eMerge owns, so a sending recorded only on the
 * board never reaches the case. The marker says so, in words; the one-click act
 * beside it records the sending as HER act, under her name -- never by itself.
 * The server reads Monday's word itself and refuses when the board does not say
 * it went to DCF (verification P2-B); nothing here sends a word.
 */
export const MONDAY_EXPLAIN = 'Monday\'s word for this UMED is further along than eMerge\'s stage. eMerge does not take stages from Monday; record it here if it is true.';

/** When only the board's sending date makes the item this work's (final verification P2-3). */
export const MONDAY_ITEM_FIRST_SEEN_BEFORE = 'This Monday item was first seen before this case opened. Make sure it is this UMED.';

/**
 * The board item "Monday says" is read from, named in the confirm panel (P2-3): its id, the board's own
 * sending date, and the day eMerge first saw it -- so she can check it is THIS UMED before recording it.
 */
export function mondayItemLine(item: MondayItem): string {
  const sent = item.sent_to_dcf_date ? `sent to DCF ${shortDate(item.sent_to_dcf_date)}`
    : item.submitted_date ? `submitted ${shortDate(item.submitted_date)}` : 'no sending date on the board';
  const seen = item.first_seen_on || item.first_seen_at;
  return `Monday item ${item.monday_item_id || 'not recorded'} · ${sent} · first seen ${seen ? shortDate(seen) : 'date not recorded'}`;
}

/**
 * Accept Monday's word (ruling #10, Isaiah Cotto, 2026-10-07; Josie's point 10: SUBMITTED in eMerge while Monday
 * says REVERSAL). One click on the marker opens the native act eMerge records that word with -- the SAME act, panel
 * and server call as the status tile (a reversal asks who and why, as it always does) -- under her name. Nothing is
 * written until she records it in that panel, and eMerge never applies the word on its own.
 */
export const ACCEPT_MONDAY_EXPLAIN = 'Opens the eMerge act that records this word, under your name. Nothing changes until you record it.';
function AcceptMondayWord({ word, name, onAccept }: { word: string; name: string; onAccept: () => void }) {
  return (
    <button type="button" className="um-link um-acceptmonday" title={ACCEPT_MONDAY_EXPLAIN}
      aria-label={`Accept Monday's word ${word} for ${name}: opens the eMerge act`}
      onClick={(e) => { e.stopPropagation(); onAccept(); }}>
      Accept Monday&rsquo;s word
    </button>
  );
}

/** What the word-only marker means (director's ruling, 2026-10-06): Monday moved on; nothing here follows it. */
export const MONDAY_WORD_EXPLAIN = 'Monday now says this word for the same work. eMerge has not recorded it, and nothing moves on its own: record the act here if it is right.';

export function MondayDiffers({ row, canMark, onDone, explain = false, compact = false, accept = null }: {
  row: Pick<BoardRow, 'id' | 'first_name' | 'last_name' | 'monday_differs'> & { monday_word_differs?: BoardRow['monday_word_differs']; packet_preparation?: BoardRow['packet_preparation'] };
  canMark: boolean;
  onDone: (msg: string) => void;
  /** Say why in visible words (the case panel has the room; the row keeps it short). P2-9. */
  explain?: boolean;
  /** In the row's status cell, beside the UMED status: one line, the act beside the chip (2026-10-06, P2-6). */
  compact?: boolean;
  /**
   * Ruling #10 (Isaiah Cotto, 2026-10-07): "Accept Monday's word" -- opens the SAME native act a status tile opens
   * for that word (acceptActFor). Absent where no native act maps to the word or the act is not allowed here.
   */
  accept?: { onAccept: () => void } | null;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const opener = React.useRef<HTMLButtonElement | null>(null);
  const refocus = React.useRef(false);
  useEffect(() => { setConfirming(false); setErr(null); }, [row.id]);
  useEffect(() => {
    if (!confirming && refocus.current) { refocus.current = false; opener.current?.focus(); }
  }, [confirming]);
  const d = row.monday_differs;
  if (!d) {
    // Monday says another word for the same work (not a later stage): shown beside eMerge's word, never applied.
    const w = row.monday_word_differs;
    if (!w) return null;
    return (
      <span className={`um-mondaydiff${compact ? ' compact' : ''}`} onClick={(e) => e.stopPropagation()}>
        <span className="um-tag attn" title={MONDAY_WORD_EXPLAIN}>Monday says: {w.word}</span>
        {explain && <span className="um-mondaydiff-why">{MONDAY_WORD_EXPLAIN}</span>}
        {accept && <AcceptMondayWord word={w.word} name={personName(row)} onAccept={accept.onAccept} />}
      </span>
    );
  }
  const name = personName(row);
  const cancel = () => { setErr(null); refocus.current = true; setConfirming(false); };
  const markable = canMark && d.can_mark_submitted;
  return (
    <span className={`um-mondaydiff${compact ? ' compact' : ''}`} onClick={(e) => e.stopPropagation()}>
      <span className="um-tag attn" title={MONDAY_EXPLAIN}>
        Monday says: {d.word}
      </span>
      {explain && <span className="um-mondaydiff-why">{MONDAY_EXPLAIN}</span>}
      {markable && !confirming && (
        <button type="button" className="um-link" ref={opener} onClick={() => { setErr(null); setConfirming(true); }}
          aria-label={`Mark ${name} submitted to DCF`}>Mark submitted to DCF</button>
      )}
      {/* A sending word has its own act above (Mark submitted); any other word Monday is further along with may be accepted. */}
      {accept && !(canMark && d.can_mark_submitted) && !confirming && <AcceptMondayWord word={d.word} name={name} onAccept={accept.onAccept} />}
      {confirming && (
        <div className="um-rowpanel um-confirm" role="group" aria-label={`Mark ${name} submitted to DCF`}
          onKeyDown={busy ? undefined : escapeCancels(cancel)}>
          <p className="um-rowpanel-what">
            Moves {name} to UMED submitted under your name and starts the ten-day check. Nothing is sent.
          </p>
          {d.item && <p className="um-rowpanel-item">{mondayItemLine(d.item)}</p>}
          {d.item?.matched_by === 'SENDING_DATE' && <p className="um-rowpanel-warn" role="note">{MONDAY_ITEM_FIRST_SEEN_BEFORE}</p>}
          {err && <p className="um-notice stop" role="alert">{err}</p>}
          <div className="um-rowpanel-acts">
            <button type="button" className="um-open um-primary" aria-disabled={busy} autoFocus onClick={async () => {
              if (busy) return;
              setBusy(true); setErr(null);
              try {
                const r = await umedApi.markSubmitted(row.id);
                if (!r.success) { setErr(refusal(r)); return; }
                setConfirming(false);
                onDone(`${name} is recorded as submitted to DCF.`);
              } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
            }}>{busy ? 'Recording…' : 'Mark submitted'}</button>
            <button type="button" className="um-link" disabled={busy} onClick={cancel}>Cancel</button>
          </div>
        </div>
      )}
    </span>
  );
}

/**
 * DCF RESUB NEEDED, the board's lime-green move (Josie Evans, 2026-10-06):
 * "after 10-14 days I leave Gwynn a note and click DCF RESUB NEEDED". One
 * click and an optional note; Gwynn (whoever submits) is told in-app with the
 * note; the case moves to the DCF Resub section and STAYS in Review for
 * Approvals. Offered only where the server will take it: not approved, not
 * closed, FLMMIS not reflected, no reversal or correction open, and not
 * already DCF RESUB NEEDED.
 */
/*
 * Who is told (verification of 676fdb9, P2-4): the case's submitter AND every holder of umed.submission.prepare in
 * the building, so the panel names the role, not one person.
 */
export const DCF_RESUB_EFFECT = 'The submitter, and everyone who prepares submissions for this building, is told in eMerge, with your note, to send the UMED to DCF again. The case moves to the DCF RESUB NEEDED section and stays in Review for Approvals. Reactivate undoes it.';
/** The word is on the case but no move was made here (Monday's word, or an older review's): nobody was told in eMerge. */
export const dcfResubWordOnly = (row: Pick<BoardRow, 'source_status_raw' | 'dcf_resub_moved'>): boolean =>
  normalizeMondayWord(row.source_status_raw) === 'DCF RESUB NEEDED' && !row.dcf_resub_moved;
export function dcfResubOffered(row: BoardRow, canDispose: boolean): boolean {
  if (!canDispose || row.stage === 'CLOSED' || isApproved(row)) return false;
  if (row.reversal || row.correction) return false;
  // The move already made is not offered twice; a word nobody acted on here IS offered the move (P2-1).
  if (row.dcf_resub_moved) return false;
  // The server's own answer, and the only one: it reads the real FLMMIS, which a viewer without approval detail sees as WITHHELD
  // (verification of PR #359, P2). A row that does not carry it offers nothing.
  if (!row.resubmission || !row.resubmission.can_open) return false;
  const openBoardException = row.exception && row.exception !== 'NONE' && row.exception !== 'CROSSOVER' && row.exception !== 'RESUBMISSION' && row.exception_state !== 'COMPLETE';
  return !openBoardException;
}

function DcfResubPanel({ row, onClose }: { row: BoardRow; onClose: () => void }) {
  const acts = React.useContext(BoardActs);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const key = React.useRef(freshKey());
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await umedApi.dcfResubmission(row.id, { note: note.trim() || null, expected_updated_at: row.updated_at || null, idempotency_key: key.current });
      if (!r.success) { setErr(refusal(r)); return; }
      onClose();
      const told = r.data && r.data.notified ? r.data.notified.recipients : null;
      acts?.announce(`${personName(row)} is DCF RESUB NEEDED.${told ? ' The submitter has been told.' : ''} The case stays in Review for Approvals.`, row.id);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };
  return (
    <div className="um-rowpanel um-resubpanel" role="group" aria-label={`Move ${personName(row)} to DCF RESUB NEEDED`}
      onClick={(e) => e.stopPropagation()} onKeyDown={busy ? undefined : escapeCancels(onClose)}>
      <p className="um-rowpanel-what">
        <FilledStatusChip word="DCF RESUB NEEDED" column={UMED_STATUS} size="sm" /> {DCF_RESUB_EFFECT}
      </p>
      <label className="um-rowfield"><span>Note to the submitter</span>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} rows={2} disabled={busy}
          placeholder="Optional: what DCF said, or what to send" aria-label="Note to the submitter (optional)" />
      </label>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        <button type="button" className="um-open um-primary" disabled={busy} onClick={save}>{busy ? 'Recording…' : 'Move to DCF RESUB NEEDED'}</button>
        <button type="button" className="um-link" disabled={busy} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

/**
 * The UMED status chip IS the control (Josie Evans, round 4, 2026-10-07, point 6).
 *
 * On Monday she clicks the coloured status and picks the next one from a grid of coloured labels. Here the
 * chip opens the same kind of grid: each tile filled with the Monday colour of the word it produces
 * (mondayPalette), and each tile the SAME native act the "Record…" select it replaces offered -- nothing is
 * declared by being picked; the act's own panel still says what will happen before it happens. A tile the
 * server would refuse for this case and this viewer is not shown (outcomesFor, dcfResubOffered, the
 * capability flags), and a chip with nothing to offer is not a button at all.
 *
 * Keyboard: the chip is a button (Enter or Space opens), arrow keys move through the tiles (left/right one,
 * up/down a row), Home/End jump, Escape closes, and focus goes back to the chip -- on Escape, and when the act's
 * panel is cancelled.
 */
export interface ActTile { act: RowAct; label: string; hex: string | null }
export function actTilesFor(row: BoardRow, acts: BoardActsValue | null): ActTile[] {
  const tiles: ActTile[] = outcomesFor(row, acts).map((o) => {
    const label = outcomeLabel(o, row);
    return { act: o as RowAct, label, hex: mondayHex(UMED_STATUS, label) };
  });
  if (acts?.canMarkSubmitted && row.stage === 'READY' && !isApproved(row)
    && !row.sent_to_dcf_on && !row.submitted_on
    && !['NO_LONGER_QUALIFIES', 'NEEDS_RECERT', 'DECEASED'].includes(row.lifecycle || '')) {
    tiles.unshift({ act: 'SUBMISSION', label: 'Record sending…', hex: null });
  }
  // PROBLEM ACCOUNT is a disposition (reason required, Reactivate undoes it). Not on an approved case (P2-2).
  const problem = Boolean(acts?.canDispose) && row.stage !== 'CLOSED' && row.disposition?.word !== 'PROBLEM_ACCOUNT' && !isApproved(row);
  if (dcfResubOffered(row, Boolean(acts?.canDispose))) tiles.push({ act: 'DCF_RESUB', label: 'DCF RESUB NEEDED', hex: mondayHex(UMED_STATUS, 'DCF RESUB NEEDED') });
  if (problem) tiles.push({ act: 'PROBLEM_ACCOUNT', label: 'PROBLEM ACCOUNT', hex: mondayHex(UMED_STATUS, 'PROBLEM ACCOUNT') });
  // The other dispositions are not one word: an uncoloured tile that opens the disposition panel.
  if (acts?.canDispose) tiles.push({ act: 'DISPOSITION', label: 'Disposition…', hex: null });
  return tiles;
}

/**
 * The native act that records Monday's word on this row, as the tiles offer it (ruling #10): the tile whose word IS
 * Monday's word, or -- for a disposition word (DECEASED, PAID IN FULL, ...) -- the disposition panel opened on it,
 * where the panel would offer it. Null when no native act maps to the word, or the act is not allowed here.
 */
export function acceptActFor(row: BoardRow, acts: BoardActsValue | null): { act: RowAct; disposition?: Disposition } | null {
  const raw = row.monday_differs?.word || row.monday_word_differs?.word || null;
  const word = normalizeMondayWord(raw);
  if (!word) return null;
  const tiles = actTilesFor(row, acts);
  const tile = tiles.find((t) => t.act !== 'DISPOSITION' && normalizeMondayWord(t.label) === word);
  if (tile) return { act: tile.act };
  if (!tiles.some((t) => t.act === 'DISPOSITION') || row.stage === 'CLOSED') return null;
  const d = (Object.keys(DISPOSITION_STATUS_WORD) as Disposition[]).find((k) => normalizeMondayWord(DISPOSITION_STATUS_WORD[k]) === word);
  if (!d || row.disposition?.word === d || (isApproved(row) && CONTRADICTS_APPROVAL.includes(d))) return null;
  return { act: 'DISPOSITION', disposition: d };
}

/** Two tiles a row, as Monday's label picker lays them out at this size. */
const ACT_GRID_COLUMNS = 2;

export function ActGrid({ name, tiles, onPick, onClose, id }: {
  name: string; tiles: ActTile[]; onPick: (act: RowAct) => void; onClose: () => void; id: string;
}) {
  const [at, setAt] = useState(0);
  const refs = React.useRef<Array<HTMLButtonElement | null>>([]);
  useEffect(() => { refs.current[at]?.focus(); }, [at]);
  const move = (to: number) => setAt(Math.max(0, Math.min(tiles.length - 1, to)));
  const onKeyDown = (e: React.KeyboardEvent) => {
    const k = e.key;
    if (k === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); return; }
    if (k === 'Tab') { onClose(); return; }
    const step = k === 'ArrowRight' ? 1 : k === 'ArrowLeft' ? -1 : k === 'ArrowDown' ? ACT_GRID_COLUMNS : k === 'ArrowUp' ? -ACT_GRID_COLUMNS : 0;
    if (step) { e.preventDefault(); move(at + step); return; }
    if (k === 'Home') { e.preventDefault(); move(0); return; }
    if (k === 'End') { e.preventDefault(); move(tiles.length - 1); }
  };
  return (
    <div className="um-actgrid" role="menu" id={id} aria-label={`Record for ${name}`} onKeyDown={onKeyDown}
      onClick={(e) => e.stopPropagation()}>
      {tiles.map((t, i) => (
        <button key={t.act} type="button" role="menuitem" data-act={t.act} tabIndex={i === at ? 0 : -1}
          ref={(el) => { refs.current[i] = el; }}
          className={`um-acttile${t.hex ? ' filled' : ''}`} style={t.hex ? filledChipStyle(t.hex) : undefined}
          onFocus={() => { if (at !== i) setAt(i); }}
          onClick={() => onPick(t.act)}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

function StatusCell({ row }: { row: BoardRow }) {
  const acts = React.useContext(BoardActs);
  const [outcome, setOutcome] = useState<RowAct | ''>('');
  const [open, setOpen] = useState(false);
  // The disposition Accept Monday's word opened the panel on (ruling #10); null when the panel opened from the tile.
  const [initialDisposition, setInitialDisposition] = useState<Disposition | null>(null);
  const chip = React.useRef<HTMLButtonElement | null>(null);
  const refocus = React.useRef(false);
  // A different resident in this cell's position is a different case.
  useOnRowChange(row.id, () => { setOutcome(''); setOpen(false); });
  // A grid closed with Escape, or a panel cancelled, gives focus back to the chip (P2-1).
  useEffect(() => {
    if (!outcome && !open && refocus.current) { refocus.current = false; chip.current?.focus(); }
  }, [outcome, open]);
  const backToChip = () => { refocus.current = true; setOutcome(''); };
  const closeGrid = () => { refocus.current = true; setOpen(false); };
  const tiles = actTilesFor(row, acts);
  const accepting = acceptActFor(row, acts);
  const approved = (msg: string) => acts?.announce(msg, row.id);
  const name = personName(row);
  const gridId = `um-actgrid-${row.id}`;
  const chipEl = <StatusChip word={row.source_status_raw} legend={row.status_legend} source={row.status_word_source} approvedInEmerge={isApproved(row)} />;
  // A press anywhere else closes the grid (focus stays where that press put it).
  const cell = React.useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e: MouseEvent) => { if (cell.current && !cell.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);
  return (
    <div className="um-statuscell" ref={cell}>
      {tiles.length > 0 && !outcome ? (
        <button type="button" className={`um-statusbtn${open ? ' open' : ''}`} ref={chip}
          aria-haspopup="menu" aria-expanded={open} aria-controls={open ? gridId : undefined}
          aria-label={`Record an outcome for ${name}`} title="Choose what to record"
          onClick={(e) => { e.stopPropagation(); if (open) closeGrid(); else setOpen(true); }}>
          {chipEl}
          <span className="um-statusbtn-caret" aria-hidden="true">▾</span>
        </button>
      ) : chipEl}
      {/* Not when it only repeats the status word beside it (verification of 676fdb9, P2-4): DCF RESUB NEEDED twice says nothing more. */}
      {!dispositionRepeatsWord(row) && <DispositionChip disposition={row.disposition} />}
      {/* The word with no move behind it: said, so nobody assumes the submitter was told (P2-1). */}
      {dcfResubWordOnly(row) && <span className="um-quiet um-wordonly">Not moved in eMerge: nobody was told here.</span>}
      {/*
        * Monday says a later stage (2026-10-06, P2-6): beside the UMED status, where Josie reads at 1440, as a compact
        * chip -- the eMerge stage column it used to sit in is off the right edge of the screen on every tab she works.
        */}
      <MondayDiffers row={row} compact canMark={Boolean(acts?.canMarkSubmitted)} onDone={(m) => acts?.announce(m, row.id)}
        accept={accepting && !outcome ? { onAccept: () => { setOpen(false); setInitialDisposition(accepting.disposition || null); setOutcome(accepting.act); } } : null} />
      {acts?.canReview && row.approval.ready_to_approve && !outcome && !open && <ApproveAct row={row} compact onDone={approved} />}
      {open && !outcome && (
        <ActGrid name={name} tiles={tiles} id={gridId} onClose={closeGrid}
          onPick={(a) => {
            setOpen(false);
            if (a === 'SUBMISSION') { acts?.openCase(row.id); return; }
            setInitialDisposition(null); setOutcome(a);
          }} />
      )}
      {outcome === 'APPROVAL' && <ApproveAct row={row} compact startConfirming onCancel={backToChip} onDone={(m) => { setOutcome(''); approved(m); }} />}
      {outcome === 'DISPOSITION' && <DispositionPanel row={row} initial={initialDisposition} onClose={backToChip} />}
      {outcome === 'PROBLEM_ACCOUNT' && <DispositionPanel row={row} initial="PROBLEM_ACCOUNT" onClose={backToChip} />}
      {outcome === 'DCF_RESUB' && <DcfResubPanel row={row} onClose={backToChip} />}
      {outcome && outcome !== 'APPROVAL' && outcome !== 'DISPOSITION' && outcome !== 'PROBLEM_ACCOUNT' && outcome !== 'DCF_RESUB' && <OutcomePanel row={row} outcome={outcome as Outcome} onClose={backToChip} />}
    </div>
  );
}

/**
 * Josie Evans, 2026-09-24: "Where is says last follow up - I want it to pull
 * up a calendar so I can put the date in that I last checked this patient for
 * an approval. When I click on it now, I'm unable to change anything."
 *
 * Setting the date IS recording a review: she looked, and the facts had not
 * moved. So the cell posts exactly that — the two facts carried forward
 * unchanged, dated the day she looked — which re-arms the ten-day clock from
 * that day and leaves the check in the chronology with her name on it. A date
 * written straight into the column would have been a number with no act
 * behind it, and the projector would have had every right to overwrite it.
 *
 * Not offered on an approved case: once FLMMIS is reflected there is nothing
 * left to follow up, and the date is history.
 */
/**
 * The day she last checked. Only that.
 *
 * Josie Evans, 2026-09-28: "Last follow up date. I just need the Calendar. It
 * pulls way too many options down. I just want to click the date and move on.
 * I can record NOCA and FLMMIS in a different category."
 *
 * This cell shipped that morning as a three-field review -- date, NOCA and
 * FLMMIS together -- because the three are recorded by one API call and it
 * seemed tidy to ask for them at once. It was tidy for the code and wrong for
 * the person: she checks a dozen cases in a row and changes the approval facts
 * on almost none of them, so two thirds of the panel was furniture in the way
 * of a date.
 *
 * The three facts are still ONE call, because they are one review and the
 * server's chronology should say so. The date picker simply sends the NOCA and
 * FLMMIS the row already has, unchanged, and the two of them get their own
 * cells beside this one where they can be changed deliberately.
 */
function FollowUpCell({ row }: { row: BoardRow }) {
  const acts = React.useContext(BoardActs);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /*
   * The day she just recorded, held until the board's refetch catches up.
   *
   * Josie Evans, 2026-09-29: "When I click on the last follow up date. It goes
   * back to the original date. It's not letting me update it to today's date
   * unless I click on 'NOCA received'."
   *
   * The server was never the problem -- posting her exact payload to those
   * exact cases returns 201. The cell was: on success it closed the editor and
   * re-rendered `row.last_follow_up_on`, which is a PROP and still held the old
   * date, because `reload()` had not come back yet. So the old date reappeared
   * the instant she picked a new one. Changing the NOCA "fixed" it only by
   * causing a second refetch.
   *
   * A control must never show a value it knows is stale. What she recorded is
   * shown immediately, and released once the row agrees.
   */
  const [justSet, setJustSet] = useState<string | null>(null);
  // Set / Change takes focus back when the picker closes -- Cancel, Escape or a recorded day (P2-4).
  const back = useReturnFocus<HTMLButtonElement>(open);
  useEffect(() => { setOpen(false); setErr(null); }, [row.id]);
  useEffect(() => { setJustSet(null); }, [row.id, row.last_follow_up_on]);
  const close = () => { back.restore(); setOpen(false); setErr(null); };

  const on = justSet || row.last_follow_up_on;
  const shown = on ? <span>{shortDate(on)}</span> : <NotRecorded />;
  // Offered on every open case. A case already approved still gets checked --
  // that is how a NOCA that arrives after the approval gets recorded.
  if (!acts?.canReview || row.stage === 'CLOSED') return shown;

  const record = async (on: string) => {
    setBusy(true); setErr(null);
    try {
      /*
       * A follow-up, not a review.
       *
       * This used to carry the two approval facts forward through the review
       * vocabulary, which has three words per fact where the case has five.
       * `nocaForward('REJECTED')` and `flmmisForward('EXCEPTION')` both return
       * null, and `|| 'NOT_RECORDED'` turned "I cannot say this" into "it is
       * not recorded". On 2026-09-30 that erased FLMMIS EXCEPTION from ten
       * cases -- every one that had it -- and dropped them out of Josie's
       * queue mid-morning.
       *
       * Recording the day she looked now asserts nothing about what she
       * found, which is the truth of what she did.
       */
      const r = await umedApi.recordFollowUp(row.id, on);
      if (!r.success) { setErr(refusal(r)); return; }
      setJustSet(on);
      setOpen(false);
      // Said in the board's status line; focus back on Set / Change once the board has re-read (P2-4).
      acts?.announce(`Last follow-up for ${personName(row)} is recorded as ${shortDate(on)}.`, row.id, { focusTo: () => back.opener.current });
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <span className="um-followup">
        {shown}
        <button type="button" className="um-link" disabled={busy} ref={back.opener} onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          aria-label={`Record the day you last checked on ${personName(row)}`}>
          {on ? 'Change' : 'Set'}
        </button>
        {err && <span className="um-notice stop" role="alert">{err}</span>}
      </span>
    );
  }

  /*
   * A date input and nothing else. Picking a day records it -- no Record
   * button to find, because choosing the date IS the whole act.
   */
  return (
    <span className="um-followup" onClick={(e) => e.stopPropagation()}>
      <input
        type="date" className="um-dateonly" autoFocus disabled={busy}
        defaultValue={on ? String(on).slice(0, 10) : todayLocal()}
        max={todayLocal()}
        aria-label={`The day you last checked on ${personName(row)}`}
        onChange={(e) => { if (e.target.value) record(e.target.value); }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { e.stopPropagation(); close(); }
          if (e.key === 'Enter') { const v = (e.target as HTMLInputElement).value; if (v) record(v); }
        }}
      />
      {/*
        * One click for the answer she gives most of the time.
        *
        * The picker opens ON today, so choosing today fires no change event and
        * the control appeared to do nothing — worst exactly where the cell is
        * empty and "Set" is the first thing she reaches for. Writing the test
        * for the revert fix is what surfaced it.
        */}
      <button type="button" className="um-link um-today" disabled={busy}
        aria-label={`Record today as the day you last checked on ${personName(row)}`}
        onClick={() => record(todayLocal())}>Today</button>
      <button type="button" className="um-link" disabled={busy} onClick={close}>Cancel</button>
      {err && <span className="um-notice stop" role="alert">{err}</span>}
    </span>
  );
}

/**
 * Attach a file where it is read (Josie Evans, round 4, 2026-10-07, point 5): "no file · Attach" under the NOCA
 * and under FLMMIS, as one compact drop zone. Click (or Enter/Space on Attach) to pick, or drag a file onto it;
 * it uploads at once -- no dialog, no class to choose, the column IS the class. Images and PDFs only, as the
 * picker offers; the server checks the bytes again. The result is said in the board's status line, and focus
 * comes back to this zone's Attach after the re-read.
 */
export const DROP_ACCEPT = 'application/pdf,image/*';
export const acceptsFile = (f: Pick<File, 'type' | 'name'>): boolean =>
  f.type === 'application/pdf' || f.type.startsWith('image/') || (!f.type && /\.(pdf|png|jpe?g|gif|webp|tiff?|heic)$/i.test(f.name));

/** What eMerge holds, in words: On file, no file, or nothing said when it is not known (or not for this viewer). */
function HeldWords({ held, what }: { held: boolean | null | undefined; what: string }) {
  if (held === true) return <span className="um-tag ok" title={`eMerge holds ${what} itself.`}>On file</span>;
  if (held === false) return <span className="um-sub">no file</span>;
  return null;
}

export function DropAttach({ row, documentClass, what, held }: {
  row: Pick<BoardRow, 'id' | 'first_name' | 'last_name'>;
  documentClass: 'NOCA' | 'FLMMIS';
  /** How the file is named to her: "NOCA", "FLMMIS evidence". */
  what: string;
  held: boolean | null | undefined;
}) {
  const acts = React.useContext(BoardActs);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = React.useRef<HTMLInputElement | null>(null);
  const attachRef = React.useRef<HTMLButtonElement | null>(null);
  useOnRowChange(row.id, () => { setErr(null); setOver(false); });
  const name = personName(row);

  const upload = async (file: File) => {
    if (!acceptsFile(file)) { setErr('Images and PDFs only.'); return; }
    setBusy(true); setErr(null);
    try {
      const r = await umedApi.uploadDocument(row.id, documentClass, file, freshKey());
      if (!r.success) { setErr(refusal(r)); return; }
      acts?.announce(`The ${what} for ${name} is attached.`, row.id, { focusTo: () => attachRef.current });
    } catch { setErr('Could not reach the server. Nothing was uploaded.'); } finally { setBusy(false); }
  };
  const dragging = (e: React.DragEvent) => {
    if (busy || !Array.from(e.dataTransfer?.types || []).includes('Files')) return;
    e.preventDefault(); e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    setOver(true);
  };
  return (
    <span className={`um-drop${over ? ' over' : ''}${busy ? ' busy' : ''}${held === true ? ' held' : ''}`} data-class={documentClass}
      onClick={(e) => e.stopPropagation()}
      onDragEnter={dragging} onDragOver={dragging}
      onDragLeave={(e) => { if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) setOver(false); }}
      onDrop={(e) => {
        e.preventDefault(); e.stopPropagation(); setOver(false);
        const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        if (f && !busy) upload(f);
      }}>
      <HeldWords held={held} what={`the ${what}`} />
      {held !== true && held !== false && <span className="um-sub">{what}</span>}
      <span aria-hidden="true" className="um-drop-sep">·</span>
      <button type="button" className="um-link um-drop-pick" disabled={busy} ref={attachRef}
        onClick={() => fileRef.current?.click()}
        aria-label={`Attach ${documentClass === 'NOCA' ? 'a NOCA' : 'FLMMIS evidence'} for ${name}: choose a file, or drop an image or PDF here`}>
        {busy ? 'Uploading…' : over ? 'Drop to attach' : 'Attach'}
      </button>
      <input ref={fileRef} type="file" className="um-filehidden" tabIndex={-1} accept={DROP_ACCEPT}
        aria-hidden="true" data-testid={`um-drop-input-${documentClass.toLowerCase()}`}
        onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) upload(f); }} />
      {err && <span className="um-notice stop" role="alert">{err}</span>}
    </span>
  );
}

/**
 * NOCA: the state, and the notice itself.
 *
 * Josie Evans, 2026-09-28: "If I can have a box to put the NOCA and just
 * upload it." Uploading a NOCA was already supported end to end -- the class,
 * the route, the capability and the API call all existed -- and the board
 * never offered it, so the only way to attach one was to open the case.
 *
 * eMerge holds 2,328 NOCAs as of that morning, 92 of them fetched that day by
 * the importer, so the cell says whether the notice is in hand rather than
 * only what somebody typed.
 */
function NocaCell({ row }: { row: BoardRow }) {
  const acts = React.useContext(BoardActs);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Where focus goes after the board re-reads (P2-4): the NOCA select (the "Record NOCA received" button is gone
  // once the NOCA is recorded), and the Attach button after an upload.
  const selectRef = React.useRef<HTMLSelectElement | null>(null);
  useEffect(() => { setErr(null); }, [row.id]);

  const state = nocaForward(row.approval.noca) || 'NOT_RECORDED';
  const held = row.documents?.noca;
  /*
   * UM-R3 (Josie Evans, 2026-10-02): Jerry Whitehead's NOCA was uploaded and
   * this cell said "Not recorded". The notice is EVIDENCE, not a review: the
   * cell says it is on file and nobody has recorded reading it, and offers the
   * one click that does -- the same native review as the select, carrying
   * FLMMIS through (and standing down where it cannot).
   */
  const onFileUnreviewed = row.noca_evidence === 'DOCUMENT_ON_FILE';
  // An approved case keeps the evidence it was approved on (P1-A): the control stands down and says why.
  const locked = isApproved(row);

  const set = async (next: NocaResult) => {
    /*
     * A review states BOTH facts, so changing the NOCA must carry the FLMMIS
     * faithfully. Where the case holds a FLMMIS a review has no word for --
     * EXCEPTION -- it cannot, and it stands down rather than writing
     * "not recorded" over it. That coercion cost ten cases their EXCEPTION on
     * 2026-09-30.
     */
    if (flmmisForward(row.approval.flmmis) === null) {
      setErr(`FLMMIS is ${FLMMIS_LABEL[row.approval.flmmis as keyof typeof FLMMIS_LABEL] || row.approval.flmmis}, which a review has no word for. Open the case to record the NOCA.`);
      return;
    }
    setBusy(true); setErr(null);
    try {
      const r = await umedApi.review(row.id, {
        noca: next,
        flmmis: flmmisForward(row.approval.flmmis) as FlmmisResult,
        decision: 'CONTINUE',
      });
      if (!r.success) { setErr(refusal(r)); return; }
      acts?.announce(`NOCA ${NOCA_RESULT_WORDS[next].toLowerCase()} is recorded for ${personName(row)}.`, row.id, { focusTo: () => selectRef.current });
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };

  return (
    <span className="um-noca" onClick={(e) => e.stopPropagation()}>
      {onFileUnreviewed ? (
        <>
          <span className="um-tag attn" title="eMerge holds the notice. Nobody has recorded reading it yet, so it is not counted as received.">On file · not yet reviewed</span>
          {acts?.canReview && row.stage !== 'CLOSED' && !locked && (
            <button type="button" className="um-link um-nocarecord" disabled={busy} onClick={() => set('RECEIVED')}
              aria-label={`Record NOCA received for ${personName(row)}`}>{busy ? 'Recording…' : 'Record NOCA received'}</button>
          )}
        </>
      ) : (
        <>
          {acts?.canReview && row.stage !== 'CLOSED' ? (
            <select value={state} disabled={busy || locked} aria-label={`NOCA for ${personName(row)}`} ref={selectRef}
              aria-describedby={locked ? `um-lock-noca-${row.id}` : undefined}
              onChange={(e) => set(e.target.value as NocaResult)}>
              {(['NOT_RECORDED', 'PENDING', 'RECEIVED'] as NocaResult[]).map((k) => (
                <option key={k} value={k}>{NOCA_RESULT_WORDS[k]}</option>
              ))}
            </select>
          ) : <span>{NOCA_RESULT_WORDS[state]}</span>}
        </>
      )}
      {locked && acts?.canReview && row.stage !== 'CLOSED' && <LockedNote id={`um-lock-noca-${row.id}`} />}
      {/* The NOCA arrived after the approval (2026-10-06): its own narrow act, where the server offers it. */}
      {locked && acts?.canReview && <LateNocaAct row={row} onDone={(m) => acts.announce(m, row.id)} />}

      {/* Round 4 (2026-10-07): the notice itself, dropped or picked, uploaded at once. Only the words when she may not attach. */}
      {acts?.canDocument && row.stage !== 'CLOSED'
        ? <DropAttach row={row} documentClass="NOCA" what="NOCA" held={held} />
        : <HeldWords held={held} what="the notice" />}
      {err && <span className="um-notice stop" role="alert">{err}</span>}
    </span>
  );
}

/**
 * FLMMIS: the state, and what FLMMIS showed.
 *
 * FLMMIS is a state in Florida's Medicaid system, not a document anyone
 * issues, so the STATE is still what Josie records (Reflected makes the case
 * ready to approve; the approval is the Approve act in the UMED status cell).
 * Round 4 (2026-10-07, point 5): she also keeps what she saw there -- a
 * screenshot or a PDF -- as the approval's evidence, attached like the NOCA
 * (document class FLMMIS, migration 20261007b). The file never sets the state:
 * FLMMIS stays the system of record. On an approved case the state is kept as
 * the approval left it (P1-A).
 */
function LockedNote({ id }: { id: string }) {
  return (
    <span className="um-locked" id={id} title={APPROVED_LOCK}>
      Approved · kept as recorded<span className="um-sr-only">. To undo an approval, record a reversal or correction.</span>
    </span>
  );
}

/**
 * A NOCA that arrives after the approval (2026-10-06).
 *
 * The NOCA select is locked on an approved row ("Approved · kept as
 * recorded"), because a review restates both facts and the server refuses it
 * there. The notice still arrives, often days after FLMMIS reflected and the
 * approval. So the row offers the one act that records it -- NOCA received,
 * with the day it came, and nothing else -- where the server says it can work
 * (`approval.late_noca_recordable`: approved, NOCA not in yet, this viewer's
 * approval scope). A small confirmation in the row, like every other row act;
 * the result is said in the board's status line and focus goes back to the row.
 */
export const LATE_NOCA_EFFECT = (name: string) =>
  `Records the NOCA for ${name} as received. The case stays approved: FLMMIS, the status, the stage and the Finance handoff do not change.`;

export function LateNocaAct({ row, onDone }: {
  row: Pick<BoardRow, 'id' | 'first_name' | 'last_name' | 'approval'>;
  onDone: (msg: string) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [on, setOn] = useState(todayLocal());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const opener = React.useRef<HTMLButtonElement | null>(null);
  const refocus = React.useRef(false);
  useEffect(() => { setConfirming(false); setErr(null); }, [row.id]);
  // Cancel or Escape gives focus back to the button that opened it (P2-1, P2-2).
  useEffect(() => {
    if (!confirming && refocus.current) { refocus.current = false; opener.current?.focus(); }
  }, [confirming]);
  if (!row.approval.late_noca_recordable) return null;
  const name = personName(row);
  const cancel = () => { setErr(null); refocus.current = true; setConfirming(false); };
  if (!confirming) {
    return (
      <button type="button" className="um-link um-nocarecord" ref={opener}
        onClick={(e) => { e.stopPropagation(); setErr(null); setOn(todayLocal()); setConfirming(true); }}
        aria-label={`Record NOCA received for ${name}`}>Record NOCA received</button>
    );
  }
  return (
    <div className="um-rowpanel um-confirm" role="group" aria-label={`Record NOCA received for ${name}`}
      onClick={(e) => e.stopPropagation()} onKeyDown={busy ? undefined : escapeCancels(cancel)}>
      <p className="um-rowpanel-what">{LATE_NOCA_EFFECT(name)}</p>
      <label className="um-rowfield"><span>The day it arrived</span>
        <input type="date" value={on} max={todayLocal()} disabled={busy}
          onChange={(e) => setOn(e.target.value)} aria-label="The day the NOCA arrived" />
      </label>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        <button type="button" className="um-open um-primary" aria-disabled={busy} autoFocus disabled={!on}
          onClick={async () => {
            // aria-disabled while busy, so a refusal leaves focus on this button rather than the page body.
            if (busy || !on) return;
            setBusy(true); setErr(null);
            try {
              const r = await umedApi.recordLateNoca(row.id, { received_on: on });
              if (!r.success) { setErr(refusal(r)); return; }
              setConfirming(false);
              onDone(r.data?.replayed
                ? `The NOCA for ${name} was already recorded as received.`
                : `NOCA received is recorded for ${name}. The case stays approved.`);
            } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
          }}>{busy ? 'Recording…' : 'Record NOCA received'}</button>
        <button type="button" className="um-link" disabled={busy} onClick={cancel}>Cancel</button>
      </div>
    </div>
  );
}

function FlmmisCell({ row }: { row: BoardRow }) {
  const acts = React.useContext(BoardActs);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /*
   * Josie Evans, 2026-10-06: "I want to be able to click down on FLMMIS
   * reflecting, but I do not want it to move to approval." Choosing Reflected
   * no longer approves anything, and no browser dialog asks about it: the row
   * opens a confirmation that says exactly what it records, and the approval
   * stays her separate act (ApproveAct, in the status cell).
   */
  const [pending, setPending] = useState<FlmmisResult | null>(null);
  const select = React.useRef<HTMLSelectElement | null>(null);
  const refocus = React.useRef(false);
  useEffect(() => { setErr(null); setPending(null); }, [row.id]);
  // Cancel or Escape gives focus back to the select that opened the confirmation (P2-1, P2-2).
  useEffect(() => {
    if (!pending && refocus.current) { refocus.current = false; select.current?.focus(); }
  }, [pending]);

  const state = flmmisForward(row.approval.flmmis) || 'NOT_RECORDED';
  // Round 4 (2026-10-07): what FLMMIS showed, attached like the NOCA. Withheld (null) where approval detail is.
  const flmmisHeld = row.documents ? row.documents.flmmis : undefined;
  const evidence = acts?.canDocument && row.stage !== 'CLOSED'
    ? <DropAttach row={row} documentClass="FLMMIS" what="FLMMIS evidence" held={flmmisHeld} />
    : <HeldWords held={flmmisHeld} what="FLMMIS evidence" />;
  if (!acts?.canReview || row.stage === 'CLOSED') {
    return <span className="um-flmmis">{FLMMIS_RESULT_WORDS[state]}{evidence}</span>;
  }
  const locked = isApproved(row);
  const name = personName(row);
  const cancel = () => { refocus.current = true; setPending(null); };
  const record = async (next: FlmmisResult) => {
    // Same rule in reverse: never write "not recorded" over a NOCA a review cannot express (REJECTED).
    if (nocaForward(row.approval.noca) === null) {
      setErr(`NOCA is ${NOCA_LABEL[row.approval.noca as keyof typeof NOCA_LABEL] || row.approval.noca}, which a review has no word for. Open the case to record the FLMMIS.`);
      return;
    }
    setBusy(true); setErr(null);
    try {
      const r = await umedApi.review(row.id, {
        noca: nocaForward(row.approval.noca) as NocaResult,
        flmmis: next, decision: 'CONTINUE',
      });
      if (!r.success) { setErr(refusal(r)); return; }
      const confirmed = pending !== null;
      setPending(null);
      // The confirmation closes under her: say what was recorded and where the next act is, and keep focus on the row.
      acts?.announce(next === 'REFLECTED'
        ? `FLMMIS reflected is recorded for ${name}. ${FLMMIS_NEXT}`
        : `FLMMIS ${FLMMIS_RESULT_WORDS[next].toLowerCase()} is recorded for ${name}.`, row.id, { keepFocus: !confirmed });
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };
  return (
    <span className="um-flmmis" onClick={(e) => e.stopPropagation()}>
      <select value={pending || state} disabled={busy || Boolean(pending) || locked} ref={select} aria-label={`FLMMIS for ${name}`}
        aria-describedby={locked ? `um-lock-flmmis-${row.id}` : undefined}
        onChange={(e) => {
          const next = e.target.value as FlmmisResult;
          if (next === state) return;
          // Reflected is the one answer that changes what Josie may do next, so it is confirmed in the row first.
          if (next === 'REFLECTED') { setErr(null); setPending(next); return; }
          record(next);
        }}>
        {(['NOT_RECORDED', 'NOT_REFLECTED', 'REFLECTED'] as FlmmisResult[]).map((k) => (
          <option key={k} value={k}>{FLMMIS_RESULT_WORDS[k]}</option>
        ))}
      </select>
      {locked && <LockedNote id={`um-lock-flmmis-${row.id}`} />}
      {evidence}
      {pending && (
        <div className="um-rowpanel um-confirm" role="group" aria-label={`Record FLMMIS reflected for ${name}`}
          onKeyDown={busy ? undefined : escapeCancels(cancel)}>
          <p className="um-rowpanel-what">{FLMMIS_EFFECT} {FLMMIS_NEXT}</p>
          <div className="um-rowpanel-acts">
            <button type="button" className="um-open um-primary" aria-disabled={busy} autoFocus onClick={() => { if (!busy) record(pending); }}>
              {busy ? 'Recording…' : 'Record FLMMIS reflected'}
            </button>
            <button type="button" className="um-link" disabled={busy} onClick={cancel}>Cancel</button>
          </div>
        </div>
      )}
      {err && <span className="um-notice stop" role="alert">{err}</span>}
    </span>
  );
}

/**
 * The Medicaid number, entered where it is read.
 *
 * The column shipped on 2026-09-24 saying the number is "entered here once
 * and copied afterwards" — and there was nowhere to enter it. The route was
 * built and nothing called it, so the column could only ever have shown a
 * dash. A promise in a tooltip is not a control.
 */
function MedicaidCell({ row }: { row: BoardRow }) {
  const acts = React.useContext(BoardActs);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(row.medicaid_number || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { setOpen(false); setValue(row.medicaid_number || ''); setErr(null); }, [row.id, row.medicaid_number]);
  const back = useReturnFocus<HTMLButtonElement>(open);

  const shown = row.medicaid_number
    ? <><span className="um-mono">{row.medicaid_number}</span><CopyCell value={row.medicaid_number} what="the Medicaid number" /></>
    : <NotRecorded why="Not recorded yet. CounselEAR does not send it, so it is entered here once and copied afterwards." />;

  if (!acts?.canMedicaid) return <span className="um-medicaid">{shown}</span>;
  if (!open) {
    return (
      <span className="um-medicaid">
        {shown}
        <button type="button" className="um-link" ref={back.opener} onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          aria-label={`${row.medicaid_number ? 'Change' : 'Record'} the Medicaid number for ${personName(row)}`}>
          {row.medicaid_number ? 'Change' : 'Record'}
        </button>
      </span>
    );
  }
  return (
    <div className="um-rowpanel" onClick={(e) => e.stopPropagation()}
      onKeyDown={busy ? undefined : escapeCancels(() => { back.restore(); setOpen(false); setErr(null); })}>
      <label className="um-rowfield"><span>Medicaid #</span>
        <input value={value} onChange={(e) => setValue(e.target.value)} maxLength={40} disabled={busy} autoFocus
          aria-label="Medicaid number" placeholder="CounselEAR: Employee ID" />
      </label>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        <button type="button" className="um-open um-primary" disabled={busy || !value.trim()} onClick={async () => {
          setBusy(true); setErr(null);
          try {
            const r = await umedApi.recordMedicaidNumber(row.id, value.trim());
            if (!r.success) { setErr(refusal(r)); return; }
            setOpen(false);
            acts.announce(`The Medicaid number for ${personName(row)} is recorded.`, row.id, { focusTo: () => back.opener.current });
          } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
        }}>{busy ? 'Recording…' : 'Record'}</button>
        <button type="button" className="um-link" disabled={busy} onClick={() => { back.restore(); setOpen(false); setErr(null); }}>Cancel</button>
      </div>
    </div>
  );
}

/**
 * A Patient Advocate sent this one over, and nobody has taken it.
 *
 * Isaiah Cotto, 2026-09-28: "Gwynn is missing the PAs submissions and needs
 * them to execute."
 *
 * Measured the same day, because the obvious explanations were all wrong.
 * They are not buried: all sixteen sit on PAGE ONE of UMEDs Needed,
 * positions 1 to 23. She was not un-told: nine in-platform notifications and
 * nine emails reached her, every one DELIVERED. The queue and the notice both
 * worked.
 *
 * What is true is that FIVE of the sixteen exist nowhere but eMerge -- no
 * Monday row, none coming, because eMerge never writes to that board. A
 * person working from Monday cannot see them at all, and a row that looks
 * like every other row gives her no reason to think otherwise.
 *
 * So the row says what it is. Not a new queue, not another notification --
 * the thing she is looking at, saying out loud that an advocate handed this
 * over, how long ago, and that it is still nobody's.
 */
function FromAnAdvocate({ row }: { row: BoardRow }) {
  const waiting = row.case_created_at;
  if (row.source_system !== 'EMERGE' || !row.claimable) return null;
  const days = waiting ? Math.floor((Date.now() - new Date(waiting).getTime()) / 86400000) : null;
  return (
    <span className="um-fromadvocate"
      title={`A Patient Advocate handed this over${days !== null && days >= 0 ? ` ${days === 0 ? 'today' : days === 1 ? '1 day ago' : `${days} days ago`}` : ''} and nobody has taken it yet.`}>
      From an advocate
      {days !== null && days >= 0 && <> · {days === 0 ? 'today' : `${days}d`}</>}
      {' · unclaimed'}
    </span>
  );
}

/**
 * The same resident, open on another UMED case.
 *
 * Josie Evans, 2026-09-28: "This patient is in here twice. Not sure if he's
 * been submitted or not. Same with Paticia Sena, Wilson Rudolph."
 *
 * Eleven residents are in there twice, and the second record usually knows
 * the answer she wants: one says Created, the other says Submitted to DCF.
 * Rather than make her find the twin by scrolling, the row carries what the
 * twin says.
 *
 * It sits in the frozen patient column on purpose — it is the one cell on
 * screen whatever the horizontal scroll is doing, and a duplicate you have to
 * scroll to notice is a duplicate you act on twice.
 */
/*
 * Josie Evans, 2026-10-02: "Some of this information under the patients names
 * are not necessary." The line under the name -- "Also open: APPROVED · sent
 * Oct 13, 2025" -- is gone from the row. What it said is not: the row carries
 * a small count ("1 other case") that opens the case, and the opened case lists
 * each of the resident's other open UMEDs with its word, its date and who
 * moves it (OtherOpenList), which is everything the line and its tooltip said.
 */
const sayOther = (o: OtherUmed) => o.word || STAGE_LABEL[o.stage as keyof typeof STAGE_LABEL] || o.stage;
const datedOther = (o: OtherUmed) => (o.sent_to_dcf_on ? `sent ${shortDate(o.sent_to_dcf_on)}`
  : o.submitted_on ? `submitted ${shortDate(o.submitted_on)}` : null);

function AlsoOpen({ others, onOpen, name }: { others?: OtherUmed[] | null; onOpen: () => void; name: string }) {
  if (!others || !others.length) return null;
  const label = `${others.length} other ${others.length === 1 ? 'case' : 'cases'}`;
  return (
    <button type="button" className="um-tag um-alsoopen-chip" onClick={(e) => { e.stopPropagation(); onOpen(); }}
      aria-label={`${label} open for ${name} — open the case to see them`} title="Open the case to see the resident's other open UMEDs">
      {label}
    </button>
  );
}

/**
 * Two open cases for this resident (#363 round 3): a Monday item was linked to the native case, the native case was
 * suspended so the item got its own case, and the native case came back. Nothing closes either on its own -- closing
 * is a person's act -- so both rows carry this, and the duplicate query lists the pair (RELINK_AFTER_REACTIVATION).
 */
export const TWO_OPEN_EXPLAIN = 'This resident has two open UMED cases: the eMerge case was reactivated after the Monday item got a case of its own. Decide which one is the work; nothing is closed automatically.';
function TwoOpenFlag() {
  return <span className="um-tag attn um-twoopen" title={TWO_OPEN_EXPLAIN}>Two open cases for this resident<span className="um-sr-only">. {TWO_OPEN_EXPLAIN}</span></span>;
}

/**
 * Where a search result lives (design verification 2026-10-06, P2-6). A search
 * answers from every case, so a row no longer says which tab it belongs to by
 * being in it. The server names the views whose predicate the row meets
 * (`board_views`, the tabs' own predicates); a closed case says Closed; a case
 * only Main table holds says its eMerge stage.
 */
function RowWhere({ row, tabs }: { row: BoardRow; tabs: BoardTabDef[] }) {
  if (row.stage === 'CLOSED') {
    return <span className="um-tag um-rowwhere closed" title="Closed: in no view, found by search and in All cases.">Closed</span>;
  }
  const views = (row.board_views || []).map((k) => tabs.find((t) => t.key === k)?.label || k);
  const text = views.length ? views.join(' · ') : `Main table · ${stageLabelOf(row.stage, isApproved(row))}`;
  return (
    <span className="um-tag um-rowwhere" title={views.length ? `In ${views.join(', ')}` : 'In no view but Main table'}>
      <span className="um-sr-only">In </span>{text}
    </span>
  );
}

/** The resident's other open UMEDs, shown with the opened case: the word, the date, who moves it. */
function OtherOpenList({ others }: { others?: OtherUmed[] | null }) {
  if (!others || !others.length) return null;
  return (
    <section className="um-othercases" aria-label="Other open UMEDs for this resident">
      <h3>Also open for this resident</h3>
      <ul>
        {others.map((o, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={i}>
            <strong>{sayOther(o)}</strong>
            {datedOther(o) && <> · {datedOther(o)}</>}
            {' · '}{o.source === 'EMERGE' ? 'eMerge' : 'the board'}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Who manages the resident's funds — readable, and now changeable.
 *
 * Josie Evans, 2026-09-29: "The who manages funds box: I need to also be able
 * to update this in case something has changed. There is no where for me to
 * change the status."
 *
 * The column shipped on 2026-09-23 read-only, so a facility that handed the
 * money to a family could only be corrected by a Patient Advocate on another
 * screen — or not at all, where no PA case existed.
 *
 * It writes the SAME resident-scoped row the PA side writes. Two surfaces, one
 * truth. It never touches `patients.payor_source`: that column is MONDAY
 * authority, and the write would be refused and then overwritten by the next
 * full refresh. Where the board used a word eMerge cannot map, the word is
 * shown AS WRITTEN and recording a real one replaces it.
 */
function FundsCell({ row }: { row: BoardRow }) {
  const acts = React.useContext(BoardActs);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fr = row.financial_responsibility;
  const [type, setType] = useState<string>(fr?.type || '');
  const [ach, setAch] = useState<boolean>(Boolean(fr?.ach_on_file));
  /* Held until the board's refetch catches up — the same staleness that made
   * the follow-up date appear to revert on 2026-09-29. */
  const [just, setJust] = useState<{ type: string; ach: boolean } | null>(null);
  useEffect(() => { setOpen(false); setErr(null); setJust(null); setType(fr?.type || ''); setAch(Boolean(fr?.ach_on_file)); }, [row.id, fr?.type, fr?.ach_on_file]);
  const back = useReturnFocus<HTMLButtonElement>(open);
  const cancel = () => { back.restore(); setOpen(false); setErr(null); };

  const shownType = just ? just.type : fr?.type || null;
  const shownAch = just ? just.ach : Boolean(fr?.ach_on_file);
  const shown = (
    <span>
      {shownType
        ? FINANCIAL_RESPONSIBILITY_LABEL[shownType as FinancialResponsibilityType] || shownType
        : fr?.raw && !shownAch
          ? <span title="The board used a word eMerge does not map.">{fr.raw}</span>
          : <NotRecorded />}
      {shownAch && <span className="um-sub">ACH on file</span>}
    </span>
  );

  if (!acts?.canTerms || row.stage === 'CLOSED') return shown;
  if (!open) {
    return (
      <span className="um-followup">
        {shown}
        <button type="button" className="um-link" disabled={busy} ref={back.opener} onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          aria-label={`Change who manages the funds for ${personName(row)}`}>Change</button>
        {err && <span className="um-notice stop" role="alert">{err}</span>}
      </span>
    );
  }
  return (
    <div className="um-rowpanel" onClick={(e) => e.stopPropagation()} onKeyDown={busy ? undefined : escapeCancels(cancel)}>
      <label className="um-rowfield"><span>Who manages the funds</span>
        <select value={type} disabled={busy} autoFocus aria-label={`Who manages the funds for ${personName(row)}`}
          onChange={(e) => setType(e.target.value)}>
          <option value="">Choose…</option>
          {(Object.keys(FINANCIAL_RESPONSIBILITY_LABEL) as FinancialResponsibilityType[]).map((k) => (
            <option key={k} value={k}>{FINANCIAL_RESPONSIBILITY_LABEL[k]}</option>
          ))}
        </select>
      </label>
      <label className="um-rowcheck">
        <input type="checkbox" checked={ach} disabled={busy} onChange={(e) => setAch(e.target.checked)} />
        <span>ACH on file</span>
      </label>
      <p className="um-rowpanel-what">Recorded against the resident, so the Patient Advocate sees the same answer.</p>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        <button type="button" className="um-open um-primary" disabled={busy || !type} onClick={async () => {
          setBusy(true); setErr(null);
          try {
            const r = await umedApi.recordFinancialResponsibility(row.id, { type, ach_on_file: ach, idempotency_key: freshKey() });
            if (!r.success) { setErr(refusal(r)); return; }
            setJust({ type, ach });
            setOpen(false);
            acts?.announce(`Who manages the funds for ${personName(row)} is recorded: ${FINANCIAL_RESPONSIBILITY_LABEL[type as FinancialResponsibilityType] || type}${ach ? ', ACH on file' : ''}.`,
              row.id, { focusTo: () => back.opener.current });
          } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
        }}>{busy ? 'Recording…' : 'Record'}</button>
        <button type="button" className="um-link" disabled={busy} onClick={cancel}>Cancel</button>
      </div>
    </div>
  );
}

/**
 * The terms, and a way to change them.
 *
 * Josie Evans, 2026-09-28: "I must be able to update them. Just because we
 * submit for terms to start in September does not mean DCF will approve for
 * that month. I need to be able to go in and change the months if necessary
 * or if they approved for different terms."
 *
 * What is shown is the later fact: terms a UMED operator settled if there are
 * any, otherwise the advocate's snapshot from when the lead closed, otherwise
 * the board's own sentence — quoted, never parsed, because deriving a number
 * from it would be putting a figure on a submission nobody made.
 *
 * Recording does not overwrite the sale. That figure is what was SOLD and it
 * feeds Finance and the monthly sales projection.
 */
/**
 * Terms: she types the months, and nothing else.
 *
 * Josie Evans, 2026-09-28: "On the submitted terms -- it pulls down a long box
 * of questions. It would be easier if I can just switch the months to what I
 * need it to be. Right now, I can click on it and just type in it instead of
 * doing extra steps."
 *
 * The panel shipped that morning asking four questions: months, each month,
 * starting, ending. Only the first is a decision -- DCF approves a NUMBER OF
 * MONTHS against a total that was already settled when the lead closed.
 *
 * Her own examples prove the arithmetic is ours to do, not hers. Every line
 * she pasted carries the same total against a different term:
 *
 *     4 months at $1,449.25 = $5,797        9 months at $644.11 = $5,797
 *     6 months at   $966.16 = $5,797        5 months at $1,159.40 = $5,797
 *
 * That is `monthly = total / months`, rounded to the cent, with the TOTAL held
 * fixed -- which is why 6 x $966.16 comes to $5,796.96 and the line still says
 * $5,797. The total is the sale, not the product, and the board has always
 * shown it that way.
 *
 * So eMerge derives what it can derive and asks only for what it cannot know.
 * Where no sale total is held -- and 2,879 open cases have none -- it cannot
 * do the arithmetic, and it asks for the monthly figure rather than invent
 * one. Start and end dates move behind "Dates", because DCF approving a
 * different month is the uncommon case and it should not tax the common one.
 */
/*
 * == One box (Josie Evans, 2026-10-02) ====================================
 *
 * "I also need to be able to type in the UMED terms and months if needed to be
 * updated. Just let me type in the box, I do not need different boxes to click
 * on for months/payments each month."
 *
 * So the editor is ONE text box, "Submitted terms", holding the line as she
 * writes it -- `9/1/26-3/1/27 (7 months at $828.14 = $5,797)`, or just `7 months
 * at 828.14`. The line is stored verbatim and the row shows it as typed. What
 * eMerge read from it is echoed underneath BEFORE it is recorded: months, the
 * amount, the dates. Where it reads only the months and a closed lead total is
 * held, the amount is worked out exactly as before (floored, the total held).
 * Where it reads nothing, the line is still kept, as typed, with no numbers --
 * the server never refuses an unread line.
 *
 * The structured fields are not gone: they are under "Details", for the rare
 * case she wants to set a number the line does not say. Used, they set the
 * numbers and re-write the line to match, so the row never shows one thing and
 * stores another.
 */
const boardSentence = (months: number, monthlyCents: number, totalCents: number | null, startsOn: string | null, endsOn: string | null): string => {
  const dates = startsOn && endsOn ? `${boardDate(startsOn)}-${boardDate(endsOn)} ` : startsOn ? `${boardDate(startsOn)} ` : '';
  return `${dates}(${months} ${months === 1 ? 'month' : 'months'} at ${cents(monthlyCents)}${totalCents !== null ? ` = ${cents(totalCents)}` : ''})`;
};

function TermsCell({ row }: { row: BoardRow }) {
  const acts = React.useContext(BoardActs);
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(false);
  const t = row.submitted_terms;
  const totalCents = row.sale_amount === null || row.sale_amount === undefined ? null : Math.round(Number(row.sale_amount) * 100);
  const lineNow = (): string => (t?.text
    || (t && t.months !== null && t.monthly_cents !== null
      ? boardSentence(t.months, t.monthly_cents, totalCents, t.starts_on || null, t.ends_on || null)
      : '')
    || row.source_terms_raw || '');
  const [line, setLine] = useState(lineNow);
  const [months, setMonths] = useState(String(t?.months ?? ''));
  const [monthly, setMonthly] = useState(t && t.monthly_cents !== null ? (t.monthly_cents / 100).toFixed(2) : '');
  const [range, setRange] = useState(() => {
    const a = t?.starts_on ? String(t.starts_on).slice(0, 10) : '';
    const b = t?.ends_on ? String(t.ends_on).slice(0, 10) : '';
    return a && b ? `${a} to ${b}` : a || '';
  });
  const [note, setNote] = useState('');
  /*
   * Whether a Details field has been EDITED (verification of 1d86ec3). Opening
   * the fold changes nothing; editing a field there sets the numbers and
   * re-writes the line; typing in the line again hands the numbers back to the
   * server's reading of it.
   */
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setOpen(false); setDetails(false); setTouched(false); setErr(null); setLine(lineNow()); }, [row.id]);
  // Change takes focus back when the editor closes: Cancel, Escape, or recorded (P2-4).
  const back = useReturnFocus<HTMLButtonElement>(open);
  const cancel = () => { back.restore(); setOpen(false); setErr(null); };

  /* The Details fold, as before: months typed, the amount derived from the closed lead total or typed. */
  const m = Number(months);
  const monthsOk = Number.isInteger(m) && m >= 1 && m <= 60;
  /*
   * FLOOR, not round. Every sentence Josie pasted floors to the cent:
   * 5,797 / 6 is 966.1667 and the board has always said $966.16, never
   * $966.17. The remainder stays with the total, which is what was sold.
   */
  const derivedCents = totalCents !== null && monthsOk ? Math.floor(totalCents / m) : null;
  const cents_ = derivedCents !== null ? derivedCents : (Number.isFinite(Number(monthly)) && Number(monthly) > 0 ? Math.round(Number(monthly) * 100) : null);
  const parsedDates = parseTermDates(range);
  const datesOk = !range.trim() || parsedDates !== null;
  const detailsUsed = details && touched && months.trim() !== '';
  const detailsOk = monthsOk && cents_ !== null && cents_ > 0 && datesOk;

  // Details set the numbers: the line follows them, so what is shown is what is stored.
  useEffect(() => {
    if (open && touched && detailsUsed && detailsOk) {
      setLine(boardSentence(m, cents_ as number, totalCents, parsedDates ? parsedDates.starts_on : null, parsedDates ? parsedDates.ends_on : null));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [months, monthly, range, touched]);

  const shown = t
    ? (
      <span>
        {t.text
          /* The line she typed, as she typed it (2026-10-02). */
          ? <>{t.text}</>
          : (
            /*
             * The board's own sentence, on one line: "8/1/26-5/1/27 (10 months at
             * $579.70 = $5,797)" (Josie Evans, 2026-09-29). The total is the
             * SALE, not months x monthly.
             */
            <>
              {(t.starts_on || t.ends_on) && (
                <>{t.starts_on ? boardDate(t.starts_on) : '?'}-{t.ends_on ? boardDate(t.ends_on) : '?'} </>
              )}
              ({t.months} {t.months === 1 ? 'month' : 'months'} at {cents(t.monthly_cents)}
              {row.sale_amount !== null && row.sale_amount !== undefined
                && <> = {cents(Math.round(Number(row.sale_amount) * 100))}</>})
            </>
          )}
        {t.source === 'EMERGE' && <span className="um-sub">settled here</span>}
      </span>
    )
    : row.source_terms_raw
      ? <span className="um-text" title="The board's own words. eMerge did not produce this figure and does not parse it.">{row.source_terms_raw}</span>
      : <NotRecorded why="Neither eMerge nor the board holds terms for this case." />;

  if (!acts?.canTerms || row.stage === 'CLOSED') return shown;
  if (!open) {
    return (
      <span className="um-followup">
        {shown}
        <button type="button" className="um-link" ref={back.opener} onClick={(e) => { e.stopPropagation(); setLine(lineNow()); setOpen(true); }}
          aria-label={`Change the terms for ${personName(row)}`}>Change</button>
      </span>
    );
  }

  const typed = line.trim();
  const read = parseTermsLine(typed);
  /*
   * The typed path sends the LINE and nothing else: the server reads it and its
   * reading is what is stored (verification of 1d86ec3). The screen only says
   * what will be read; it never works out an amount for the line itself.
   */
  const fromLine = !detailsUsed && read.parsed;
  const tooLong = typed.length > 200;
  const ok = !tooLong && (detailsUsed ? detailsOk : typed.length > 0);

  return (
    <div className="um-rowpanel" onClick={(e) => e.stopPropagation()}>
      <label className="um-rowfield um-termsfield"><span>Submitted terms</span>
        <input value={line} disabled={busy} autoFocus maxLength={200}
          aria-label={`Submitted terms for ${personName(row)}`}
          placeholder="9/1/26-3/1/27 (7 months at $828.14 = $5,797)"
          onChange={(e) => { setLine(e.target.value); setTouched(false); }}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); cancel(); } }} />
      </label>
      {tooLong && <p className="um-notice stop" role="alert">At most 200 characters.</p>}
      {!detailsUsed && typed && (fromLine
        ? (
          <p className="um-termsline um-termsread" aria-live="polite">
            Reads as {read.months} {read.months === 1 ? 'month' : 'months'} at <strong>{cents(read.monthly_cents)}</strong>
            {read.total_cents !== null && <> = {cents(read.total_cents)}</>}
            {read.starts_on && <> · {shortDate(read.starts_on)}{read.ends_on ? <> to {shortDate(read.ends_on)}</> : null}</>}
          </p>
        )
        : read.months_only !== null && totalCents !== null
          ? <p className="um-sub um-termsread" aria-live="polite">Reads {read.months_only} {read.months_only === 1 ? 'month' : 'months'}. eMerge will work the monthly amount out from the closed lead total when it is recorded.</p>
          : <p className="um-sub um-termsread" aria-live="polite">eMerge cannot read months and a monthly amount from this, so it will be kept exactly as you typed it, with no figures.</p>)}

      {details ? (
        <div className="um-termsdetails" role="group" aria-label="Terms details">
          <label className="um-rowfield"><span>Months</span>
            <input type="number" min={1} max={60} value={months} disabled={busy}
              aria-label={`Months of terms for ${personName(row)}`}
              onChange={(e) => { setMonths(e.target.value); setTouched(true); }} />
          </label>
          {derivedCents !== null ? (
            /* The sentence she reads on the board, formed as she types. */
            <p className="um-termsline" aria-live="polite">
              {m} {m === 1 ? 'month' : 'months'} at <strong>{cents(derivedCents)}</strong> = {cents(totalCents as number)}
            </p>
          ) : (
            <label className="um-rowfield"><span>Each month</span>
              <input type="number" min="0.01" step="0.01" value={monthly} disabled={busy}
                aria-label="What DCF approved each month"
                onChange={(e) => { setMonthly(e.target.value); setTouched(true); }} />
              <span className="um-sub">No closed lead total on this case, so eMerge cannot work it out.</span>
            </label>
          )}
          <label className="um-rowfield"><span>Dates</span>
            <input value={range} disabled={busy} maxLength={40}
              aria-label={`The term dates for ${personName(row)}`}
              placeholder="8/1/26 - 5/1/27"
              onChange={(e) => { setRange(e.target.value); setTouched(true); }} />
          </label>
          {range.trim() && (parsedDates
            ? (
              <p className="um-termsline" aria-live="polite">
                {shortDate(parsedDates.starts_on)}
                {parsedDates.ends_on ? <> to {shortDate(parsedDates.ends_on)}</> : <> · no end date</>}
              </p>
            )
            : <p className="um-notice stop" role="alert">Cannot read those dates. Try 8/1/26 - 5/1/27.</p>)}
          <label className="um-rowfield"><span>Why (optional)</span>
            <input value={note} maxLength={2000} disabled={busy} onChange={(e) => setNote(e.target.value)}
              placeholder="What DCF approved" />
          </label>
        </div>
      ) : (
        <button type="button" className="um-link" disabled={busy} aria-expanded={false} onClick={() => setDetails(true)}>
          Details
        </button>
      )}
      <p className="um-rowpanel-what">Recorded beside the sale, not over it — the closed lead total stays as it was.</p>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        <button type="button" className="um-open um-primary" disabled={busy || !ok} onClick={async () => {
          setBusy(true); setErr(null);
          try {
            // Details EDITED: the numbers, said to come from there. Otherwise the line alone; the server reads it.
            const numbers = detailsUsed
              ? {
                entered: 'details' as const,
                months: m, monthly_cents: cents_ as number,
                // The total is the sale's, sent as such -- never months x monthly.
                total_cents: totalCents,
                starts_on: parsedDates ? parsedDates.starts_on : null,
                ends_on: parsedDates ? parsedDates.ends_on : null,
              }
              : {};
            const r = await umedApi.recordTerms(row.id, {
              terms_text: typed || null,
              ...numbers,
              note: note.trim() || null, idempotency_key: freshKey(),
            });
            if (!r.success) { setErr(refusal(r)); return; }
            setOpen(false);
            acts?.announce(`The terms for ${personName(row)} are recorded${typed ? `: ${typed}` : ''}.`, row.id, { focusTo: () => back.opener.current });
          } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
        }}>{busy ? 'Recording…' : 'Record'}</button>
        <button type="button" className="um-link" disabled={busy} onClick={cancel}>Cancel</button>
      </div>
    </div>
  );
}

/**
 * eMerge's stage. "Approved" when a UMED operator approved the case here; the
 * same stage without the act is the board's ("Approved in Monday · not approved in eMerge"), P2-6 and P3.
 * The Monday difference sits in the UMED status cell (StatusCell), where it is
 * on screen at 1440 -- this column is not.
 */
/**
 * P0 2026-10-07: where this episode's automatically prepared packet stands, in
 * words with a text marker (never colour alone), so UMEDs Needed tells a
 * prepared packet from one that is held, failed or still being made.
 */
const PREP_WORDS: Record<string, [string, string]> = {
  READY: ['!', 'Packet prepared - review required'], PREPARING: ['…', 'Preparing packet'], ACTION_REQUIRED: ['!', 'Action required'],
  FAILED: ['✕', 'Packet generation failed'], WITHDRAWN: ['–', 'Withdrawn'], SUBMITTED: ['✓', 'Submitted to DCF'],
};
export function PacketPrepLine({ prep }: { prep: BoardRow['packet_preparation'] }) {
  if (!prep) return null;
  const [marker, words] = prep.state === 'READY' && prep.review_recorded
    ? ['✓', 'Packet review recorded'] : PREP_WORDS[prep.state] || ['·', prep.state];
  const first = prep.state === 'ACTION_REQUIRED' && prep.blockers.length ? prep.blockers[0] : null;
  const detail = prep.state === 'READY' && prep.version
    ? ` · v${prep.version}${prep.pages ? ` · ${prep.pages} pp` : ''}${prep.revision_kind === 'FINAL' ? ' · dated for upload' : ''}`
    : prep.state === 'FAILED' && prep.error_code ? ` · ${prep.error_code}` : '';
  return (
    <span className={`um-prepline um-prep-${prep.state.toLowerCase()}`} title={first ? `${first.message} Resolved by ${PACKET_BLOCKER_OWNER_LABEL[first.owner] || first.owner}.` : undefined}>
      <span aria-hidden="true">{marker}</span> {words}{detail}
      {first ? <span className="um-quiet"> · {first.message}{prep.blockers.length > 1 ? ` (+${prep.blockers.length - 1} more)` : ''}</span> : null}
    </span>
  );
}

function StageCell({ row }: { row: BoardRow }) {
  return (
    <span className="um-stagecell">
      <span>{stageLabelOf(row.stage, isApproved(row))}</span>
      <PacketPrepLine prep={row.packet_preparation} />
    </span>
  );
}

/* ── Columns ──────────────────────────────────────────────────────────── */

/**
 * The column set, from §11. Not every tab shows every column: a table of 20
 * columns where 8 are empty is harder to reconcile than one that shows what
 * the tab is about, and §11 says so explicitly ("Do not force every tab to
 * show every column").
 *
 * `always` columns are on every tab; the rest name the tabs that carry them.
 */
interface Col {
  key: string;
  label: string;
  sort?: BoardSort;
  always?: boolean;
  tabs?: BoardTabKey[];
  /** Withheld unless the caller may read the approval facts. */
  needsApproval?: boolean;
  render: (r: BoardRow) => React.ReactNode;
}

/**
 * A dash, and on request the reason for it.
 *
 * Isaiah relaying Josie, 2026-09-23: "We also need the submitted terms
 * values, case #'s and Approval dates as of rn they are showing as -".
 *
 * Measured: submitted terms exist on 0 of 3,126 sales, case numbers on 69 of
 * 32,461 residents, and on this tab an approval date is empty BY DEFINITION
 * because the tab is cases that are not approved yet. None of those is a
 * broken column, and none of them can be filled by this screen.
 *
 * So the dash stays -- inventing a value would be worse -- but it carries the
 * reason, and the footer names the three together. A person who cannot tell a
 * missing value from a broken report will assume the report is broken, and
 * they will be right to.
 */
const NotRecorded = ({ why }: { why?: string }) => (
  <span className="um-text none" title={why}>{'—'}{why ? <span className="um-sr-only"> {why}</span> : null}</span>
);

const COLUMNS: Col[] = [
  { key: 'status', label: 'UMED status', sort: 'status', always: true, render: (r) => <StatusCell row={r} /> },
  { key: 'native-stage', label: 'eMerge stage', always: true, render: (r) => <StageCell row={r} /> },
  {
    key: 'enrollment', label: 'Enrollment', always: true,
    render: (r) => (r.enrollment_state === 'ENROLLED'
      ? <span className="um-tag ok">Enrolled{r.enrollment_confirmed_at ? ` · ${shortDate(r.enrollment_confirmed_at)}` : ''}</span>
      : <span className={r.enrollment_state === 'NOT_ENROLLED_FINAL' ? 'um-tag stop' : 'um-tag'}>{ENROLLMENT_LABEL[r.enrollment_state]}</span>),
  },
  {
    key: 'liability', label: 'Patient Liability', sort: 'liability', always: true,
    render: (r) => (r.patient_liability_cents === null
      ? <NotRecorded />
      : <span className="um-money">{cents(r.patient_liability_cents)}{r.patient_liability_status === 'VERIFIED' ? <span className="um-tag ok">Verified</span> : null}</span>),
  },
  {
    key: 'terms', label: 'Submitted terms', always: true,
    /*
     * Josie Evans, 2026-09-24: "All patients' terms are still not under
     * submitted."
     *
     * They were not. The column read only the native sale's term snapshot,
     * which EP-010 began recording on 2026-09-22 and which exists on TWO
     * rows. The board's own terms sentence is on 1,505 cases -- 215 of them
     * at SUBMITTED -- and the column never looked at it.
     *
     * So: the native snapshot when there is one, because it is structured
     * and eMerge produced it; otherwise the board's sentence, shown as the
     * board wrote it. Deriving a number from that sentence would be putting
     * a figure on a submission nobody made, so it is quoted, not parsed.
     */
    render: (r) => <TermsCell row={r} />,
  },
  {
    key: 'sale', label: 'Closed lead total', sort: 'sale', always: true,
    render: (r) => (r.sale_amount === null
      ? <NotRecorded />
      : (
        <span className="um-money">
          {dollars(r.sale_amount)}
          {r.sale_product_label && <span className="um-sub">{r.sale_product_label}</span>}
        </span>
      )),
  },
  {
    key: 'funds', label: 'Who manages the funds', always: true,
    // Josie Evans, 2026-09-23, listing her New Approvals columns in order:
    // "who manages the funds" is third. Read-only, from Patient Demographics,
    // and honest about its gaps -- four in five open cases carry nothing, and
    // a blank is shown as a blank rather than defaulted to a payor nobody
    // chose. A word the vocabulary does not hold is shown AS WRITTEN.
    render: (r) => <FundsCell row={r} />,
  },
  {
    key: 'case-number', label: 'Case #', always: true,
    // Held for 69 of 32,461 residents. The dash is the truth, and saying which
    // truth it is beats leaving Josie to wonder whether the column is broken.
    render: (r) => (r.case_number ? <span>{r.case_number}</span> : <NotRecorded why="eMerge holds a case number for very few residents. It has never been imported in bulk." />),
  },
  { key: 'dob', label: 'DOB', always: true, render: (r) => (r.resident_dob ? <span>{shortDate(r.resident_dob)}</span> : <NotRecorded />) },
  {
    key: 'ssn', label: 'SSN', always: true,
    // Isaiah, 2026-09-22: "make the SSN viewable when clicked on, not
    // necessarily out in the open ... it is very important for the work they
    // do." So the number is in the table and one click away — and never in the
    // payload that draws the table. See SsnCell.
    render: (r) => <SsnCell row={r} />,
  },
  {
    /*
     * Josie Evans, 2026-09-24: "Next to the SS# - it would be nice to have
     * the patients Medicaid # listed so I can copy & paste it instead of
     * going into CounselEAR."
     *
     * CounselEAR does not expose it -- PatientByID returns 23 fields and
     * none is an employee or Medicaid identifier -- so it is typed once here
     * and copied ever after. Shown in full: it is an identifier she already
     * reads off CounselEAR's screen, and a masked one she cannot copy would
     * defeat the entire request.
     */
    key: 'medicaid', label: 'Medicaid #', always: true,
    render: (r) => <MedicaidCell row={r} />,
  },
  {
    key: 'available', label: 'Lead available', sort: 'available', always: true,
    render: (r) => (r.lead_available
      ? (
        <span>
          {shortDate(r.lead_available.on)}
          <span className="um-sub">
            {relativeDay(r.lead_available.on)}
            {r.lead_available.basis === 'FIRST_SEEN' ? ' · first seen' : ''}
          </span>
        </span>
      )
      : <NotRecorded />),
  },
  {
    key: 'dcf', label: 'DCF account', always: true,
    render: (r) => (
      <span>
        {r.dcf_account?.email
          ? <><span className="um-mono">{r.dcf_account.email}</span><CopyCell value={r.dcf_account.email} what="the DCF account email" /></>
          : <span className="um-text none">No email</span>}
        {/*
          * Two lines, not three. Making the password button always visible on
          * 2026-09-28 put it on a line of its own, which made every row taller
          * -- and Josie's very next note was "I can still only see 3 patients
          * at once." The tallest cell sets the row height, so the button sits
          * beside the state rather than beneath it.
          *
          * The password is never printed here. "Copy password" reveals it on
          * the server, audited per reveal, and writes it straight to the
          * clipboard (Josie 2026-09-24; approved by Isaiah).
          */}
        <span className="um-sub">
          {r.dcf_account?.state === 'EMAIL_ASSIGNED' && r.dcf_account?.revealable ? <DcfLoginReady caseId={r.id} name={personName(r)} /> : <>
          {/*
            * Review for Approvals holds cases with no DCF login made (director's ruling, 2026-10-06), so the
            * cell says it plainly: no account recorded in eMerge, for this case or another of the resident's,
            * is "No account made" -- and the board's own "no DCF account" box is named as Monday's word.
            */}
          {/* "No account made" only where an account is needed and absent (verification of 676fdb9, P2-3); Not required and Unknown as before. */}
          {r.dcf_account?.need === 'PROVISIONED' ? 'Account made'
            : r.dcf_account?.need === 'ISSUE' ? 'Could not be made'
              : r.dcf_account?.need === 'NOT_REQUIRED' ? 'Not required'
                : r.dcf_account?.need !== 'NEEDED' ? 'Unknown'
                  : r.dcf_account?.source === 'EMERGE' ? 'Needed'
                    : <>No account made{r.dcf_account_state === 'REQUIRED' && <span className="um-quiet"> · Monday&rsquo;s word: no DCF account</span>}</>}
          {r.dcf_account?.state === 'ACCOUNT_CREATED' && <CopyPassword caseId={r.id} />}
          </>}
          {/* Ruling #6 (2026-10-07): Monday's DCF EMAIL CREATED = Stuck, a marker only -- shown, never applied. */}
          <MondayDcfEmailMarker row={r} />
        </span>
      </span>
    ),
  },
  {
    key: 'documents', label: 'UMED · Audiogram', always: true,
    render: (r) => (
      <span className="um-docs">
        <span className={`um-pip${r.documents?.umed ? ' on' : ''}`} aria-hidden="true">U</span>
        <span className={`um-pip${r.documents?.audiogram ? ' on' : ''}`} aria-hidden="true">A</span>
        <span className="um-sr-only">
          UMED {r.documents?.umed ? 'on file' : r.documents?.umed === null ? 'not verified' : 'not on file'};
          audiogram {r.documents?.audiogram ? 'on file' : r.documents?.audiogram === null ? 'not verified' : 'not on file'}.
        </span>
      </span>
    ),
  },
  /**
   * Josie Evans, 2026-09-28: "I can record NOCA and FLMMIS in a different
   * category" and "If I can have a box to put the NOCA and just upload it."
   *
   * Both columns already existed and were read-only -- the two facts could be
   * SEEN here and only CHANGED from inside the follow-up panel, which is why
   * that panel had to ask three questions to record a date. Each is now
   * changeable where it is read, and the follow-up went back to being a date.
   */
  {
    key: 'noca', label: 'NOCA', needsApproval: true, always: true,
    render: (r) => <NocaCell row={r} />,
  },
  {
    key: 'flmmis', label: 'FLMMIS', needsApproval: true, always: true,
    render: (r) => <FlmmisCell row={r} />,
  },
  /**
   * The board's FAXED? column, mirrored.
   *
   * April, 2026-09-24, naming what her view turns on: "Key notations:
   * STATUS TYPE OF UMED / FAXED? DISTINCTION / SENT TO DCF". Its values are
   * sentences rather than a yes or no -- "(FAXED) still NEED SSN #",
   * "Need SIGNED UMED", "NEEDS NEW UMED" -- so it is rendered as the board
   * wrote it and never reduced to a tick.
   */
  {
    key: 'faxed', label: 'Faxed?', always: true,
    render: (r) => (r.source_faxed_status_raw
      ? <span className="um-text">{r.source_faxed_status_raw}</span>
      : <NotRecorded why="The board holds no faxed note for this case." />),
  },
  { key: 'submitted-on', label: 'UMED submitted', sort: 'submitted', always: true, render: (r) => (r.submitted_on ? <span>{shortDate(r.submitted_on)}</span> : <NotRecorded />) },
  { key: 'sent-to-dcf', label: 'Sent to DCF', sort: 'sent-to-dcf', always: true, render: (r) => (r.sent_to_dcf_on ? <span>{shortDate(r.sent_to_dcf_on)}</span> : <NotRecorded />) },
  /**
   * Josie Evans, 2026-09-24: she works an approval queue "oldest 'date lead
   * closed' sale to the newest". The field was on the row and had no column,
   * so the thing she orders her day by could not be seen or sorted.
   */
  {
    key: 'lead-closed', label: 'Date lead closed', sort: 'lead-closed', always: true,
    render: (r) => (r.closed_on
      ? <span>{shortDate(r.closed_on)}</span>
      : <NotRecorded why="No closing date on this lead." />),
  },
  { key: 'last-follow-up', label: 'Last follow-up', sort: 'last-follow-up', always: true, render: (r) => <FollowUpCell row={r} /> },
  /**
   * Josie Evans, 2026-09-28: "It is also not showing me who did the sale. I
   * need to be able to see who the patient advocate is in case there are
   * questions on the account."
   *
   * The name was already on the row and had no column -- the same shape of
   * miss as "Date lead closed" four days earlier. Nothing was computed for
   * this; it was only ever hidden.
   */
  {
    key: 'advocate', label: 'Patient advocate', always: true,
    render: (r) => (r.advocate_name
      ? <span>{r.advocate_name}</span>
      : <NotRecorded why="No advocate is recorded against this case." />),
  },
  {
    key: 'approved-on', label: 'Approval date', sort: 'approved', always: true,
    // The board's APPROVED date is a source claim. It is shown, and shown to be
    // one: eMerge's own approval needs FLMMIS reflected.
    // Approved in eMerge, the date is the act's; the board's date it replaced is kept beside it (P2-3, 2026-10-06).
    render: (r) => (r.approved_on
      ? (
        <span>
          {shortDate(r.approved_on)}
          {r.approval.canonical !== 'PROVEN'
            ? <span className="um-sub">source claim</span>
            : r.board_approved_on ? <span className="um-sub">board said {shortDate(r.board_approved_on)}</span> : null}
        </span>
      )
      : <NotRecorded why="No approval date: the case is not approved in eMerge, and the board holds no date for it." />),
  },
];

/**
 * The order a tab's columns sit in, where the business has asked for one.
 *
 * Josie Evans, 2026-09-23, for New Approvals: "Patient name, Facility name,
 * who manages the funds, liability, dcf account & password next to each
 * other, terms submitted, closed lead total, NOCA, Last umed follow up date."
 *
 * Patient and Facility are not in this list because they are not in COLUMNS:
 * the patient is the frozen row header and the facility is the cell beside
 * it, so they are already first and second.
 *
 * Naming an order does NOT hide the rest. Every other column still follows,
 * in the table's own order -- a reconciliation view that silently dropped a
 * column would be worse than one with columns in the wrong place.
 */
const TAB_COLUMN_ORDER: Partial<Record<BoardTabKey, string[]>> = {
  // The status word IS this tab (SFS CORRECTION-FLMMIS ISSUE), so it leads (design verification P2-8).
  'new-approvals-noca': ['status', 'funds', 'liability', 'dcf', 'terms', 'sale', 'noca', 'last-follow-up'],
  // April's own reading order for this group, 2026-09-24: the UMED status,
  // whether it was faxed, whether it reached DCF, then the two identifiers
  // she checks a row against.
  'board-umeds-needed': ['status', 'sent-to-dcf', 'faxed', 'ssn', 'dob', 'documents', 'dcf'],
  // Oldest first is how Josie works this queue, so the date she sorts by
  // sits beside the two approval facts she is checking.
  // The UMED status (with Approve) sits right after FLMMIS: record the evidence, then approve beside it --
  // at 1280 the Approve control was off-screen behind DCF (design verification 2026-10-06, P1-a).
  'review-for-approvals': ['lead-closed', 'noca', 'flmmis', 'status', 'sent-to-dcf', 'last-follow-up', 'dcf'],
  // 2026-10-06: a completed correction is waiting on DCF like any sending -- the approval columns lead.
  'dcf-correction-completed': ['status', 'lead-closed', 'noca', 'flmmis', 'sent-to-dcf', 'last-follow-up', 'dcf'],
  // 2026-10-06: Monday says it went to DCF; the row's Mark submitted is in the status cell, beside eMerge's stage.
  'sent-in-monday': ['status', 'native-stage', 'sent-to-dcf', 'submitted-on', 'dcf', 'documents'],
  // UM-R1: a problem account goes back to the PA, so who that is and how to reach the account come first.
  'problem-accounts': ['status', 'advocate', 'dcf', 'funds', 'last-follow-up'],
};

const columnsFor = (tab: BoardTabKey, approvalReview: boolean) => {
  const shown = COLUMNS.filter((c) => (c.always || (c.tabs || []).includes(tab)) && (!c.needsApproval || approvalReview));
  const wanted = TAB_COLUMN_ORDER[tab];
  if (!wanted) return shown;
  const first = wanted.map((k) => shown.find((c) => c.key === k)).filter(Boolean) as typeof shown;
  const rest = shown.filter((c) => !wanted.includes(c.key));
  return [...first, ...rest];
};

/**
 * What the rows are, while a search is on (round 4, 2026-10-07). The number said is the number of rows, always:
 * in a tab, the tab's matching rows (residents on a work tab, cases on Main table); across every case, the cases.
 * The offer to look across every case carries the number of rows it opens.
 */
export function SearchBanner({ q, data, tabLabel, perResident, onEvery, onBack, onClear }: {
  q: string; data: BoardPage | null; tabLabel: string; perResident: boolean;
  onEvery: () => void; onBack: () => void; onClear?: () => void;
}) {
  const every = Boolean(data && data.scope === 'EVERYWHERE');
  const n = data ? data.total : null;
  const noun = every ? 'case' : perResident ? 'resident' : 'case';
  const counted = n === null ? null : `${n} ${noun}${n === 1 ? '' : 's'}`;
  const more = !every && data && typeof data.everywhere_total === 'number' ? data.everywhere_total : null;
  return (
    <div className={`um-everywhere${every ? '' : ' um-intab'}`} role="status">
      <span className="um-everywhere-mark" aria-hidden="true">⌕</span>
      {every ? (
        <span>
          <strong>Results from every case</strong> for “{q}”{counted ? <> — {counted}, open first</> : null}.
          {' '}Not only {tabLabel}; the counts on the tabs still count their own view.
        </span>
      ) : (
        <span>
          <strong>In {tabLabel}</strong>{counted ? <>: {counted} {n === 1 ? 'matches' : 'match'}</> : null} “{q}”.
          {' '}{perResident ? 'One row per resident; their other cases are behind “other cases” on the row.' : 'One row per case.'}
        </span>
      )}
      {every
        ? <button type="button" className="um-link" onClick={onBack}>Back to {tabLabel}</button>
        : more !== null && more > 0 && (
          <button type="button" className="um-link um-everybtn" onClick={onEvery}
            aria-label={`Search every case: ${more} ${more === 1 ? 'case matches' : 'cases match'} “${q}”`}>
            Search every case ({more})
          </button>
        )}
      {onClear && <button type="button" className="um-link" onClick={onClear}>Clear search</button>}
    </div>
  );
}

/* ── The table ────────────────────────────────────────────────────────── */

export default function BoardTable({ scope, tab, q, page, onTab, onPage, onSelect, selected, detail, onClearSearch }: {
  scope: Scope;
  tab: BoardTabKey;
  q: string;
  /** Clear the search and go back to the tab (2026-10-06). */
  onClearSearch?: () => void;
  page: number;
  onTab: (t: BoardTabKey) => void;
  onPage: (p: number) => void;
  onSelect: (id: string) => void;
  selected: string | null;
  /**
   * The open case, rendered UNDER its own row (Isaiah, 2026-09-22: "when
   * opening a case it should open underneath the record instead of the right
   * of the component").
   *
   * A pane beside the table cost roughly 40% of the width on every screen,
   * including while nothing was open — and on a table this wide that width is
   * the whole point. Underneath, the case also keeps its row in view, so the
   * resident you opened is still the resident you are reading.
   */
  detail?: React.ReactNode;
}) {
  const tabs: BoardTabDef[] = scope.board_tabs || [];
  const legend: LegendEntry[] = scope.status_legend || [];
  const [data, setData] = useState<BoardPage | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState<{ col: BoardSort; dir: 'asc' | 'desc' } | null>(null);

  // A different tab is a different question, so it starts unsorted rather than
  // carrying the last tab's ordering into it.
  useEffect(() => { setSort(null); }, [tab]);

  /*
   * Josie Evans, round 4 (2026-10-07), points 1 and 2: "search shows 3 in Review for Approvals" -- one case in
   * Review and two old episodes, each a full row, read as duplicates in Review. A search IN A TAB is now that
   * tab's answer: its own matching rows, one per resident, the resident's other cases behind "N other cases".
   * Every case is one explicit step away ("Search every case", with the number of rows it opens), and it ends
   * when the search or the tab changes. All cases keeps searching everything.
   */
  const [everyCase, setEveryCase] = useState(false);
  useOnRowChange(`${tab}|${q}`, () => setEveryCase(false));
  const searching = Boolean(q.trim());
  const wantEverywhere = searching && everyCase;
  // Search as you type (round 4): a newer request makes the one in flight stale -- it is cancelled, and its answer, if it
  // still arrives, is never shown over the newer one.
  const inFlight = React.useRef<AbortController | null>(null);
  const load = useCallback(async () => {
    if (inFlight.current) inFlight.current.abort();
    const ctl = typeof AbortController === 'function' ? new AbortController() : null;
    inFlight.current = ctl;
    const stale = () => inFlight.current !== ctl;
    setLoading(true); setErr(null);
    try {
      const r = await umedApi.board({ tab, q, page, sort: sort?.col ?? null, dir: sort?.dir ?? null, everywhere: wantEverywhere }, ctl ? ctl.signal : undefined);
      if (stale()) return;
      if (r.success && r.data) setData(r.data);
      else { setData(null); setErr(r.code === 'TAB_UNKNOWN' ? 'That view does not exist.' : (r.error || r.message || 'The server refused this view.')); }
    } catch { if (!stale()) { setData(null); setErr('Could not reach the server.'); } } finally { if (!stale()) setLoading(false); }
  }, [tab, q, page, sort, wantEverywhere]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (inFlight.current) inFlight.current.abort(); }, []);

  /*
   * Asked once for the whole board. A failure is `false`, not `null`: if we
   * cannot establish that the reveal is available, the screen does not offer
   * it. Refusing to offer an act we are unsure of is the safe direction for a
   * control that decrypts a social security number.
   */
  const [canReveal, setCanReveal] = useState<boolean | null>(null);
  useEffect(() => {
    let live = true;
    (async () => {
      try { const r = await residentsApi.scope(); if (live) setCanReveal(Boolean(r.success && r.data && r.data.can.reveal_ssn)); }
      catch { if (live) setCanReveal(false); }
    })();
    return () => { live = false; };
  }, []);

  /*
   * The board's one status line (design verification 2026-10-06, P1-b). Every
   * row act writes what it did here -- "Ann Nelson is approved. The Finance
   * handoff is open." -- because the control that made it is gone by the time
   * the board re-reads. Cleared first, then set, so the same sentence twice is
   * announced twice. Always in the DOM, so a screen reader is already
   * listening to it before anything is said.
   */
  const [rowNotice, setRowNotice] = useState('');
  const noticeRef = React.useRef<HTMLParagraphElement | null>(null);
  const focusAfterLoad = React.useRef<{ rowId: string; focusTo?: () => HTMLElement | null } | null>(null);
  const noticeTimer = React.useRef<number | null>(null);
  useEffect(() => () => { if (noticeTimer.current !== null) window.clearTimeout(noticeTimer.current); }, []);
  const announce = useCallback((msg: string, rowId: string, opts?: AnnounceOpts) => {
    setRowNotice('');
    if (noticeTimer.current !== null) window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => { noticeTimer.current = null; setRowNotice(msg); }, 30);
    focusAfterLoad.current = opts?.keepFocus ? null : { rowId, focusTo: opts?.focusTo };
    load();
  }, [load]);
  /*
   * After the re-read: back to the control that opened the act when it is
   * still there (P2-4), else that row's name, or -- when the act took the row
   * out of this tab -- the status line that says where it went. Never left on
   * the page body. preventScroll throughout: a re-read must not throw the
   * table sideways (P3, the late-NOCA jump).
   */
  useEffect(() => {
    const target = focusAfterLoad.current;
    if (!target || loading) return;
    focusAfterLoad.current = null;
    const own = target.focusTo ? target.focusTo() : null;
    if (own && own.isConnected && !(own as HTMLButtonElement).disabled) { own.focus({ preventScroll: true }); return; }
    const opener = Array.from(document.querySelectorAll<HTMLButtonElement>('.um-rowopen'))
      .find((b) => b.dataset.rowId === target.rowId);
    if (opener) opener.focus({ preventScroll: true });
    else noticeRef.current?.focus({ preventScroll: true });
  }, [data, loading]);

  /*
   * The selected tab is on screen (P3, 2026-10-06): at 375 the tab bar scrolls sideways and a tab opened from a
   * link -- New Approvals - NOCA is the eleventh -- sat off the right edge, so the bar showed no selected tab at all.
   * The bar scrolls itself, never the page.
   */
  const tabBar = React.useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const bar = tabBar.current;
    const on = bar?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!bar || !on) return;
    const delta = on.getBoundingClientRect().left - bar.getBoundingClientRect().left;
    if (delta < 0 || delta + on.offsetWidth > bar.clientWidth) bar.scrollLeft = Math.max(0, bar.scrollLeft + delta - 16);
  }, [tab, tabs.length]);

  const cols = columnsFor(tab, scope.approval_review);
  const badge = (t: BoardTabDef) => scope.counts.board_tabs?.[t.key as BoardTabKey];
  const everywhere = Boolean(data && data.scope === 'EVERYWHERE');
  // Clearing the search gives focus to the search box it came from, not to the page body (P2-1).
  const clearSearch = onClearSearch ? () => {
    onClearSearch();
    window.setTimeout(() => document.querySelector<HTMLInputElement>('.um-search input[type="search"]')?.focus(), 0);
  } : undefined;

  /*
   * What may be recorded from a row, asked once. `canDo` already requires
   * writes to be NATIVE, so a process with the gate shut offers nothing
   * rather than offering a control that 404s.
   */
  const boardActs: BoardActsValue = React.useMemo(() => ({
    canReview: canDo(scope, 'review_approval'),
    canCorrect: canDo(scope, 'manage_corrections'),
    canMedicaid: canDo(scope, 'prepare_submission'),
    canTerms: canDo(scope, 'prepare_submission'),
    canMarkSubmitted: canDo(scope, 'prepare_submission'),
    openCase: (id) => { if (selected !== id) onSelect(id); },
    canDocument: canDo(scope, 'add_document'),
    sfs: (scope.correction_types || []).includes('SFS_CORRECTION'),
    canDispose: canDo(scope, 'record_disposition'),
    canReverse: canDo(scope, 'manage_reversals'),
    requesters: scope.reversal_requesters || [],
    flmmisIssue: (scope.correction_types || []).includes('SFS_CORRECTION_FLMMIS_ISSUE'),
    recheckDays: scope.recheck_days || 10,
    reload: load,
    announce,
  }), [scope, load, announce, onSelect, selected]);

  return (
    <BoardActs.Provider value={boardActs}>
    <CanReveal.Provider value={canReveal}>
    <section className="um-card um-board" aria-label="Board views">
      {/* The board's own tab bar, in the board's own order. */}
      <div className="um-boardtabs" role="tablist" aria-label="Board views" ref={tabBar}>
        {tabs.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} title={`${t.what} Replaces the board's ${t.board}.`}
            className={`um-boardtab${tab === t.key ? ' on' : ''}`} onClick={() => onTab(t.key as BoardTabKey)}>
            {t.label}
            {badge(t) !== undefined && <span className="um-count">{badge(t)}</span>}
          </button>
        ))}
      </div>

      {searching && (
        <SearchBanner q={q.trim()} data={data} tabLabel={tabs.find((t) => t.key === tab)?.label || 'this tab'} perResident={(tabs.find((t) => t.key === tab)?.rows || 'RESIDENT') !== 'CASE'}
          onEvery={() => setEveryCase(true)} onBack={() => setEveryCase(false)} onClear={clearSearch} />
      )}

      <div className="um-board-head">
        {/* While the rows are every case, the tab's own description is not what they are: muted, and said so (P2-7). */}
        <p className={`why${everywhere ? ' um-muted' : ''}`}>
          {everywhere && <span className="um-sr-only">Not these rows: </span>}
          {data ? data.what : tabs.find((t) => t.key === tab)?.what}
          {data && <span className="um-text none"> Replaces the board&rsquo;s {data.board_view}; nothing is moved into a group.</span>}
        </p>
        <StatusLegend legend={legend} />
      </div>

      <p className={`um-rownotice${rowNotice ? ' on' : ''}`} role="status" aria-live="polite" tabIndex={-1} ref={noticeRef}>{rowNotice}</p>

      {err && <p className="um-notice stop" role="alert">{err}</p>}
      {!data && loading && <p className="um-text none">Loading…</p>}
      {data && data.items.length === 0 && (
        <p className="um-empty">
          {!q.trim() ? 'Nothing in this view.'
            : everywhere ? `No case in your scope matches “${q.trim()}”.`
              : `Nothing in ${tabs.find((t) => t.key === tab)?.label || 'this view'} matches “${q.trim()}”.${data.everywhere_total ? ' Search every case to see the rest.' : ''}`}
        </p>
      )}

      {data && data.sections && data.sections.length > 0 && data.scope !== 'EVERYWHERE' && (
        <ul className="um-sections" aria-label={`${data.board_view} sections`}>
          {data.sections.map((x) => (
            <li key={x.key} className="um-section-sum">
              <FilledStatusChip word={x.word} column={UMED_STATUS} label={x.label} />
              <strong className="um-section-n">{x.count}</strong>
              <span className="um-section-what">{x.what}</span>
            </li>
          ))}
        </ul>
      )}

      {data && data.items.length > 0 && (
        <div className="um-tablewrap">
          <table className="um-table um-boardtable" aria-label={`${everywhere ? 'Every case' : data.board_view} — ${data.total}${everywhere ? '' : ` case${data.total === 1 ? '' : 's'}`}`}>
            <thead>
              <tr>
                <SortHead col="resident" label="Patient" page={data} onSort={(c, d) => setSort({ col: c, dir: d })} className="um-col-who"
                  title="Spelling comes from CounselEAR — correct it there. patients.first_name and patients.last_name are EXTERNAL-authoritative, so a correction made here would be replaced by the next sync." />
                <SortHead col="facility" label="Facility" page={data} onSort={(c, d) => setSort({ col: c, dir: d })} />
                {cols.map((c) => (
                  <SortHead key={c.key} col={c.sort || null} label={c.label} page={data} onSort={(s, d) => setSort({ col: s, dir: d })} />
                ))}
              </tr>
            </thead>
            <tbody>
              {data.items.map((r, i) => (
                <React.Fragment key={r.id}>
                {/* A section starts where its first row is (Review for Approvals: DCF RESUB NEEDED, then UMED SUBMITTED TO DCF). */}
                {!everywhere && data.sections && r.review_section && (i === 0 || data.items[i - 1].review_section !== r.review_section) && (() => {
                  const x = data.sections.find((y) => y.key === r.review_section);
                  return x ? (
                    <tr className="um-sectionrow">
                      <th scope="rowgroup" colSpan={cols.length + 2}>
                        <span className="um-sectionhead">
                          <FilledStatusChip word={x.word} column={UMED_STATUS} label={x.label} />
                          <span className="um-section-n">{x.count} {x.count === 1 ? 'resident' : 'residents'}</span>
                        </span>
                      </th>
                    </tr>
                  ) : null;
                })()}
                <tr className={`um-oprow${selected === r.id ? ' on' : ''}`}
                  /*
                   * The whole record is the control (Isaiah, 2026-09-22: "Each
                   * record should be clickable and closable"). The Open button
                   * used to be the LAST column, so on a table this wide you had
                   * to scroll right to find the way in.
                   *
                   * A click that landed on something else in the row — the SSN
                   * reveal, a sort control, a link — belongs to that thing, not
                   * to the row. Nobody clicking "show me the SSN" is also asking
                   * to open the case.
                   */
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('button, a, input, select, textarea')) return;
                    // Selecting text IS the other thing people do on a table
                    // this wide. A click that ended a selection is a copy in
                    // progress, not a request to open the case -- opening one
                    // here would also blow the selection away.
                    const sel = window.getSelection();
                    if (sel && !sel.isCollapsed && sel.toString().trim()) return;
                    onSelect(r.id);
                  }}>
                  {/* Frozen identity column (§17): the name stays while the rest
                      scrolls — which is exactly why the toggle lives here. It is
                      the one cell that is on screen whatever the horizontal
                      scroll is doing. */}
                  <th scope="row" className="um-col-who">
                    <button type="button" className="um-rowopen" aria-expanded={selected === r.id} data-row-id={r.id}
                      aria-label={`${selected === r.id ? 'Close' : 'Open'} ${personName(r)}`}
                      onClick={(e) => { e.stopPropagation(); onSelect(r.id); }}>
                      <span className="um-caret" aria-hidden="true" />
                      <span className="um-rowname">
                        <strong>{personName(r)}</strong>
                        <FromAnAdvocate row={r} />
                      </span>
                    </button>
                    {/* Beside the name, never inside its button: a count, not a sentence (Josie Evans, 2026-10-02). */}
                    <AlsoOpen others={r.other_open_umeds} name={personName(r)} onOpen={() => { if (selected !== r.id) onSelect(r.id); }} />
                    {r.two_open_after_relink && <TwoOpenFlag />}
                    <CopyCell value={personName(r)} what={`the name ${personName(r)}`} />
                    {everywhere && <RowWhere row={r} tabs={tabs} />}
                  </th>
                  <td>
                    {r.facility_name ? <>{r.facility_name}<CopyCell value={r.facility_name} what="the facility name" /></> : <NotRecorded />}
                  </td>
                  {cols.map((c) => <td key={c.key}>{c.render(r)}</td>)}
                </tr>
                {selected === r.id && detail && (
                  <tr className="um-detailrow">
                    <td colSpan={cols.length + 2}>
                      <div className="um-detailwrap">
                        <OtherOpenList others={r.other_open_umeds} />
                        {detail}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && (data.total > data.limit || data.page > 1) && (
        <div className="um-pager">
          <span className="num">{(data.page - 1) * data.limit + 1}–{Math.min(data.page * data.limit, data.total)} of {data.total}</span>
          <div className="btns">
            <button type="button" onClick={() => onPage(data.page - 1)} disabled={data.page <= 1}>Previous</button>
            <button type="button" onClick={() => onPage(data.page + 1)} disabled={data.page >= Math.ceil(data.total / data.limit)}>Next</button>
          </div>
        </div>
      )}

      {data && (
        <p className="um-text none um-board-foot">
          {data.scope === 'EVERYWHERE' ? 'Search results from every case in your scope.' : <>Counted and listed by the same predicate ({data.lens}).</>} Last read {scope.counts.board_read_at ? shortDateTime(scope.counts.board_read_at) : 'never'}.
          {' '}A dash means eMerge holds no value, never that the column failed — hover one to see which.
          {/*
            * Not "empty by definition" (verification 2026-10-06, P2-5): the view is the FLMMIS-issue word, and a case
            * can carry it with the board's approval date, or after an approval here when a correction was raised.
            */}
          {tab === 'new-approvals-noca' && ' On this view an approval date marked “source claim” is the board’s date, not an approval in eMerge; a case approved here shows its own date.'}
        </p>
      )}
    </section>
    </CanReveal.Provider>
    </BoardActs.Provider>
  );
}
